import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { COMMS_CENTER_2 as CC2 } from "@/lib/flags";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import IdentityReviewQueue from "./IdentityReviewQueue";
import ComposerModal from "./ComposerModal";
import ConvertToLeadDialog from "./ConvertToLeadDialog";

const CHANNEL_META = {
  internal: { label: "Internal", icon: "Lock", tone: "bg-violet-50 text-violet-600" },
  portal: { label: "Portal", icon: "Globe", tone: "bg-violet-50 text-violet-600" },
  webchat: { label: "Website Chat", icon: "MessageSquare", tone: "bg-emerald-50 text-emerald-600" },
  email: { label: "Email", icon: "Mail", tone: "bg-blue-50 text-blue-600" },
  sms: { label: "SMS", icon: "MessageCircle", tone: "bg-amber-50 text-amber-600" },
  whatsapp: { label: "WhatsApp", icon: "MessageCircle", tone: "bg-green-50 text-green-600" },
  line: { label: "LINE", icon: "MessageCircle", tone: "bg-teal-50 text-teal-600" },
  phone_note: { label: "Phone", icon: "Phone", tone: "bg-slate-100 text-slate-600" },
  website_form: { label: "Website Form", icon: "FileInput", tone: "bg-sky-50 text-sky-600" },
  google_business: { label: "Google Business", icon: "Chrome", tone: "bg-rose-50 text-rose-600" },
  referral: { label: "Referral", icon: "UserPlus", tone: "bg-purple-50 text-purple-600" },
  manual: { label: "Manual Entry", icon: "PenLine", tone: "bg-slate-100 text-slate-600" },
  penny: { label: "Penny", icon: "Bot", tone: "bg-indigo-50 text-indigo-600" },
  system: { label: "System", icon: "Settings", tone: "bg-slate-100 text-slate-500" },
};
const ch = (k) => CHANNEL_META[k] || CHANNEL_META.system;
const PRIORITY_TONE = { high: "bg-rose-100 text-rose-700", normal: "bg-amber-50 text-amber-600", low: "bg-slate-100 text-slate-500" };
const IRP_TONE = { ok: "bg-emerald-100 text-emerald-700", warning: "bg-amber-100 text-amber-700", breached: "bg-rose-100 text-rose-700", answered: "bg-slate-100 text-slate-500", pending: "bg-slate-100 text-slate-400" };
const IRP_LABEL = { ok: "On track", warning: "Due soon", breached: "SLA breached", answered: "Responded", pending: "Pending" };
const fmtRemaining = (m) => {
  if (m == null) return "";
  const a = Math.abs(m); const s = a < 60 ? `${a}m` : `${Math.floor(a / 60)}h ${a % 60}m`;
  return m < 0 ? `${s} overdue` : `${s} left`;
};
const WAITING = { waiting_on_client: ["Waiting on Client", "text-blue-600"], waiting_on_staff: ["Waiting on Staff", "text-amber-600"], escalated: ["Escalated", "text-rose-600"], resolved: ["Resolved", "text-emerald-600"], open: ["Open", "text-slate-500"] };

// A1: Correspondent type config — authoritative identity labels for inbound email
const CORRESPONDENT_TYPE = {
  staff:           { label: "Staff / Administrator", icon: "Shield",    bg: "bg-violet-100", text: "text-violet-700" },
  active_client:   { label: "Active Client",         icon: "UserCheck", bg: "bg-blue-100",   text: "text-blue-700"   },
  inactive_client: { label: "Inactive Client",       icon: "UserMinus", bg: "bg-slate-100",  text: "text-slate-600"  },
  existing_lead:   { label: "Existing Lead",         icon: "UserPlus",  bg: "bg-amber-100",  text: "text-amber-700"  },
  new_inquiry:     { label: "New Inquiry",           icon: "Mailbox",   bg: "bg-orange-100", text: "text-orange-700" },
  unresolved:      { label: "Unresolved Identity",   icon: "HelpCircle","bg": "bg-slate-100","text": "text-slate-600" },
  spam:            { label: "Spam / Blocked",        icon: "Ban",       bg: "bg-rose-100",   text: "text-rose-700"   },
};
const ctBadge = (type, small = false) => {
  const c = CORRESPONDENT_TYPE[type];
  if (!c) return null;
  const Ic = Icons[c.icon] || Icons.Circle;
  return (
    <span className={`inline-flex items-center gap-1 font-semibold px-2 py-0.5 rounded-full ${small ? "text-[9px]" : "text-[10px]"} ${c.bg} ${c.text}`}>
      <Ic className={small ? "h-2.5 w-2.5" : "h-3 w-3"} />{c.label}
    </span>
  );
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "now"; if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`; return `${Math.floor(s / 86400)}d ago`;
};
const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();

function Avatar({ name, url, size = "h-10 w-10", color = "bg-blue-500" }) {
  if (url) return <img src={url} alt={name} className={`${size} rounded-full object-cover shrink-0`} />;
  return <div className={`${size} rounded-full ${color} text-white flex items-center justify-center text-xs font-semibold shrink-0`}>{initials(name)}</div>;
}

// ── URL-driven queue key lookup (used by sidebar navigation + URL sync) ─────
const QKEY_MAP = {
  all_comms:            { key: "all_comms", label: "All Communications", params: {} },
  inbox:                { key: "inbox", label: "Inbox", params: { view: "mine" } },
  needs_attention:      { key: "needs_attention", label: "Needs Attention", params: { view: "needs_attention" } },
  mine:                 { key: "mine", label: "My Inbox", params: { view: "mine" } },
  team:                 { key: "team", label: "Team Inbox", params: { view: "team" } },
  unassigned:           { key: "unassigned", label: "Unassigned", params: { view: "unassigned" } },
  ch_internal:          { key: "ch_internal", label: "Internal", params: { channel: "internal" } },
  int_my_messages:      { key: "int_my_messages", label: "My Messages", params: { channel: "internal", internal_type: "private" } },
  int_teams:            { key: "int_teams", label: "Team Conversations", params: { channel: "internal", internal_type: "team_room" } },
  int_announcements:    { key: "int_announcements", label: "Firm Announcements", params: { channel: "internal", internal_type: "announcement" } },
  ch_email:             { key: "ch_email", label: "Email", params: { channel: "email" } },
  ch_email_leads:       { key: "ch_email_leads", label: "Email › Leads/Prospects", params: { channel: "email", relationship: "leads" } },
  ch_email_active:      { key: "ch_email_active", label: "Email › Active Clients", params: { channel: "email", relationship: "active_clients" } },
  ch_email_inactive:    { key: "ch_email_inactive", label: "Email › Inactive Clients", params: { channel: "email", relationship: "inactive_clients" } },
  ch_portal:            { key: "ch_portal", label: "Secure Portal", params: { channel: "portal" } },
  ch_portal_leads:      { key: "ch_portal_leads", label: "Portal › Leads/Prospects", params: { channel: "portal", relationship: "leads" } },
  ch_portal_active:     { key: "ch_portal_active", label: "Portal › Active Clients", params: { channel: "portal", relationship: "active_clients" } },
  ch_portal_inactive:   { key: "ch_portal_inactive", label: "Portal › Inactive Clients", params: { channel: "portal", relationship: "inactive_clients" } },
  ch_webchat:           { key: "ch_webchat", label: "Website Chat", params: { channel: "webchat" } },
  ch_webchat_leads:     { key: "ch_webchat_leads", label: "Chat › Leads/Prospects", params: { channel: "webchat", relationship: "leads" } },
  ch_webchat_active:    { key: "ch_webchat_active", label: "Chat › Active Clients", params: { channel: "webchat", relationship: "active_clients" } },
  ch_webchat_inactive:  { key: "ch_webchat_inactive", label: "Chat › Inactive Clients", params: { channel: "webchat", relationship: "inactive_clients" } },
  ch_sms:               { key: "ch_sms", label: "SMS", params: { channel: "sms" } },
  ch_sms_leads:         { key: "ch_sms_leads", label: "SMS › Leads/Prospects", params: { channel: "sms", relationship: "leads" } },
  ch_sms_active:        { key: "ch_sms_active", label: "SMS › Active Clients", params: { channel: "sms", relationship: "active_clients" } },
  ch_sms_inactive:      { key: "ch_sms_inactive", label: "SMS › Inactive Clients", params: { channel: "sms", relationship: "inactive_clients" } },
  ch_phone:             { key: "ch_phone", label: "Phone Calls", params: { channel: "phone_note" } },
  ch_phone_leads:       { key: "ch_phone_leads", label: "Phone › Leads/Prospects", params: { channel: "phone_note", relationship: "leads" } },
  ch_phone_active:      { key: "ch_phone_active", label: "Phone › Active Clients", params: { channel: "phone_note", relationship: "active_clients" } },
  ch_phone_inactive:    { key: "ch_phone_inactive", label: "Phone › Inactive Clients", params: { channel: "phone_note", relationship: "inactive_clients" } },
  inactive_clients:     { key: "inactive_clients", label: "Inactive Client Conversations", params: { view: "inactive_clients" } },
  identity_pending:     { key: "identity_pending", label: "Identity Pending", params: {} },
  resolved:             { key: "resolved", label: "Resolved", params: { status: "resolved" } },
  archived:             { key: "archived", label: "Archived", params: { view: "archived" } },
  trash:                { key: "trash", label: "Trash", params: { view: "trash" } },
};

// ── Internal Teams Panel — renders team list for int_teams queue view ────────
function InternalTeamsPanel({ onSelectTeamRoom }) {
  const [teams, setTeams] = useState(null);
  const [busy, setBusy] = useState({});
  const load = useCallback(() => {
    api.get("/communications/internal/teams")
      .then(r => setTeams(r.data.teams || []))
      .catch(() => setTeams([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openRoom = async (team) => {
    if (busy[team.key]) return;
    if (team.room_id) { onSelectTeamRoom(team.room_id); return; }
    setBusy(p => ({ ...p, [team.key]: true }));
    try {
      const { data } = await api.post(`/communications/internal/teams/${team.key}/room`);
      onSelectTeamRoom(data.conversation_id);
      setTeams(prev => (prev || []).map(t => t.key === team.key ? { ...t, room_id: data.conversation_id } : t));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open team room");
    } finally {
      setBusy(p => ({ ...p, [team.key]: false }));
    }
  };

  if (teams === null) return (
    <div className="flex-1 flex items-center justify-center py-12">
      <Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500" />
    </div>
  );

  return (
    <div className="flex-1 overflow-y-auto tf-scroll" data-testid="cc-team-rooms-list">
      <div className="p-3">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 mb-2 px-1">Team Channels</div>
        {teams.length === 0 ? (
          <div className="text-sm text-slate-400 text-center py-8 px-4">No accessible team channels.</div>
        ) : teams.map(team => (
          <button key={team.key} onClick={() => openRoom(team)} data-testid={`cc-team-room-${team.key}`}
            disabled={!!busy[team.key]}
            className="w-full flex items-start gap-3 p-3 rounded-xl hover:bg-slate-50 border border-transparent hover:border-slate-100 transition-colors text-left mb-1 group">
            <div className="h-9 w-9 rounded-lg bg-violet-100 flex items-center justify-center shrink-0">
              {busy[team.key]
                ? <Icons.Loader2 className="h-4 w-4 text-violet-600 animate-spin" />
                : <Icons.Users className="h-4 w-4 text-violet-600" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-medium text-slate-800 truncate">{team.name}</span>
                {team.unread_count > 0 && (
                  <span className="text-[10px] bg-violet-600 text-white px-1.5 py-0.5 rounded-full font-semibold shrink-0">{team.unread_count}</span>
                )}
              </div>
              <div className="text-xs text-slate-400 truncate mt-0.5">
                {team.room_id ? (team.last_message || "No messages yet") : "Tap to start team conversation"}
              </div>
            </div>
            <Icons.ChevronRight className="h-4 w-4 text-slate-200 group-hover:text-slate-400 shrink-0 mt-1 transition-colors" />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function CommunicationsCenter({ scope }) {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const isMgmt = ["administrator", "partner", "manager"].includes(me?.role);
  const [counts, setCounts] = useState({});
  const [queue, setQueue] = useState(() =>
    scope === "client" ? { key: "mine", label: "My Inbox", params: { view: "mine" } }
    : scope === "lead"  ? { key: "new_inquiries", label: "New Inquiries", params: { inquiry: "new" } }
    : { key: "all_comms", label: "All Communications", params: {} }
  );
  const [items, setItems] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [q, setQ] = useState("");
  const [teams, setTeams] = useState([]);
  const [teamFilter, setTeamFilter] = useState(""); // used only in the "Team Inbox" queue (mgmt team picker)
  // Navigation collapsed state removed — primary sidebar handles all comms navigation
  const [showInbound, setShowInbound] = useState(false);
  const [chExpanded, setChExpanded] = useState(false); // CC-4: "More channels" expand (local UI state)
  const [channelExpanded, setChannelExpanded] = useState({}); // Unified CC: per-channel sub-filter expand
  const [closedOpen, setClosedOpen] = useState(false); // CC-4: Closed group collapse (local UI state)
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false); // CC-4: More Filters expand (local UI state)
  // UC-5A: perspective for management view-as switching
  const [perspective, setPerspective] = useState({ key: "all", label: "All Communications", params: {} });
  const [staffList, setStaffList] = useState([]);
  // Stage 3: composer state
  const [newInternalOpen, setNewInternalOpen] = useState(false);
  const [panelComposer, setPanelComposer] = useState(null); // {mode:"new"} when composing from panel
  // Inactive Client Bulk Archive state
  const [bulkArchiveOpen, setBulkArchiveOpen] = useState(false);
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [archiveCandidates, setArchiveCandidates] = useState(null);
  const title = "Communications Center";
  const scopeLabel = scope === "lead" ? "Lead Messages" : scope === "client" ? "Client Messages" : null;

  const QUEUES = isMgmt
    ? [["mine", "My Inbox", "Inbox", { view: "mine" }], ["team", "Team Inbox", "Users", { view: "team" }], ["unassigned", "Unassigned", "UserX", { view: "unassigned" }],
       ["priority", "Priority", "Star", { priority: "high" }], ["waiting_on_client", "Waiting on Client", "Clock", { status: "waiting_on_client" }],
       ["waiting_on_staff", "Waiting on Staff", "UserCog", { status: "waiting_on_staff" }], ["escalated", "Escalated", "AlertTriangle", { escalated_only: true }]]
    : [["mine", "My Inbox", "Inbox", { view: "mine" }], ["priority", "Priority", "Star", { priority: "high" }],
       ["waiting_on_client", "Waiting on Client", "Clock", { status: "waiting_on_client" }]];
  const NEW_BUSINESS = [["new_inquiries", "New Inquiries", "Flame", { inquiry: "new" }], ["awaiting_first_response", "Awaiting First Response", "Clock", { inquiry: "awaiting_first_response" }],
    ["needs_human", "Needs Human", "UserCheck", { inquiry: "needs_human" }], ["follow_up_scheduled", "Follow-up Scheduled", "CalendarClock", { inquiry: "follow_up_scheduled" }],
    ["closed_without_engagement", "Closed Without Engagement", "XCircle", { inquiry: "closed_without_engagement" }],
    ["no_response", "No Response", "Ghost", { inquiry: "visitor_no_response" }]];
  const CHANNELS = [
    ["ch_internal", "Internal", "Lock", { channel: "internal" }],
    ["ch_email", "Email", "Mail", { channel: "email" }],
    ["ch_portal", "Secure Portal", "Globe", { channel: "portal" }],
    ["ch_webchat", "Website Chat", "MessageSquare", { channel: "webchat" }],
    ["ch_sms", "SMS", "MessageCircle", { channel: "sms" }],
    ["ch_phone", "Phone Calls", "Phone", { channel: "phone_note" }],
    ["ch_website_form", "Website Form", "FileInput", { channel: "website_form" }],
    ["ch_whatsapp", "WhatsApp", "MessageCircle", { channel: "whatsapp" }],
    ["ch_line", "LINE", "MessageCircle", { channel: "line" }],
  ];
  const PENNY = [["penny_assigned", "Assigned to Penny", "Bot", { view: "penny" }], ["penny_review", "Awaiting Human Review", "UserCheck", { channel: "penny", status: "waiting_on_staff" }]];
  const RESOLVED = [["resolved", "Resolved", "CheckCircle2", { status: "resolved" }], ["archived", "Archived", "Archive", { view: "archived" }], ["trash", "Trash", "Trash2", { view: "trash" }]];

  // Unified Communications Center — external channels with CRM relationship sub-filters
  const EXTERNAL_CHANNELS = [
    { key: "ch_email",   label: "Email",        icon: "Mail",          params: { channel: "email" },      tone: "text-blue-600",
      subs: [["ch_email_leads",   "Leads/Prospects",   "UserPlus",   { channel: "email",      relationship: "leads" }],
             ["ch_email_active",  "Active Clients",    "UserCheck",  { channel: "email",      relationship: "active_clients" }],
             ["ch_email_inactive","Inactive Clients",  "UserMinus",  { channel: "email",      relationship: "inactive_clients" }]] },
    { key: "ch_portal",  label: "Secure Portal",icon: "Globe",         params: { channel: "portal" },     tone: "text-violet-500",
      subs: [["ch_portal_leads",   "Leads/Prospects",  "UserPlus",  { channel: "portal",     relationship: "leads" }],
             ["ch_portal_active",  "Active Clients",   "UserCheck", { channel: "portal",     relationship: "active_clients" }],
             ["ch_portal_inactive","Inactive Clients", "UserMinus", { channel: "portal",     relationship: "inactive_clients" }]] },
    { key: "ch_webchat", label: "Website Chat",icon: "MessageSquare",  params: { channel: "webchat" },    tone: "text-emerald-600",
      subs: [["ch_webchat_leads",   "Leads/Prospects", "UserPlus",  { channel: "webchat",    relationship: "leads" }],
             ["ch_webchat_active",  "Active Clients",  "UserCheck", { channel: "webchat",    relationship: "active_clients" }],
             ["ch_webchat_inactive","Inactive Clients","UserMinus", { channel: "webchat",    relationship: "inactive_clients" }]] },
    { key: "ch_sms",     label: "SMS",          icon: "MessageCircle", params: { channel: "sms" },        tone: "text-amber-600",
      subs: [["ch_sms_leads",   "Leads/Prospects",    "UserPlus",  { channel: "sms",        relationship: "leads" }],
             ["ch_sms_active",  "Active Clients",     "UserCheck", { channel: "sms",        relationship: "active_clients" }],
             ["ch_sms_inactive","Inactive Clients",   "UserMinus", { channel: "sms",        relationship: "inactive_clients" }]] },
    { key: "ch_phone",   label: "Phone Calls",  icon: "Phone",         params: { channel: "phone_note" }, tone: "text-slate-500",
      subs: [["ch_phone_leads",   "Leads/Prospects",  "UserPlus",  { channel: "phone_note", relationship: "leads" }],
             ["ch_phone_active",  "Active Clients",   "UserCheck", { channel: "phone_note", relationship: "active_clients" }],
             ["ch_phone_inactive","Inactive Clients", "UserMinus", { channel: "phone_note", relationship: "inactive_clients" }]] },
  ];

// Quick filter sets shown below the Email channel card
const EMAIL_QUICK_FILTERS = [
  ["qf_all_email", "All Email", { channel: "email" }],
  ["qf_new_inq", "New Inquiries", { channel: "email", inquiry: "new" }],
  ["qf_clients", "Clients", { channel: "email", correspondent_type: "active_client" }],
  ["qf_leads", "Leads", { channel: "email", correspondent_type: "existing_lead" }],
  ["qf_staff_int", "Staff & Internal", { channel: "email", correspondent_type: "staff" }],
  ["qf_spam", "Spam / Blocked", { channel: "email", view: "blocked" }],
];

const VIEWS = [
  ["view_all", "All Conversations", "MessagesSquare", {}],
];
// More Filters — compact expandable section for secondary/less-frequent filters
const MORE_FILTERS = [
  ["view_clients", "Clients", "UserCheck", { correspondent_type: "active_client" }],
  ["view_leads", "Leads", "UserPlus", { correspondent_type: "existing_lead" }],
  ["inactive_clients", "Inactive Client Conversations", "UserMinus", { view: "inactive_clients" }, "inactive_client_linked"],
  ["waiting_on_staff", "Waiting on Staff", "UserCog", { status: "waiting_on_staff" }],
  ["awaiting_first_response", "Awaiting First Response", "Clock", { inquiry: "awaiting_first_response" }],
  ["follow_up_scheduled", "Follow-up Scheduled", "CalendarClock", { inquiry: "follow_up_scheduled" }],
  ["closed_without_engagement", "Closed Without Engagement", "XCircle", { inquiry: "closed_without_engagement" }],
  ["view_spam", "Spam / Blocked", "ShieldX", { view: "blocked" }],
];
  // Channel key → icon tone for visual differentiation in sidebar
  const CH_TONE = {
    ch_internal: "text-violet-600",
    ch_email: "text-blue-600",
    ch_webchat: "text-emerald-600",
    ch_sms: "text-amber-600",
    ch_phone: "text-slate-500",
    ch_portal: "text-violet-500",
    ch_website_form: "text-sky-600",
    ch_google: "text-rose-500",
    ch_whatsapp: "text-green-600",
    ch_line: "text-teal-600",
  };
  const LEAD_CH = ["ch_internal", "ch_webchat", "ch_website_form", "ch_google", "ch_email", "ch_sms", "ch_whatsapp", "ch_line", "ch_phone"];
  const CLIENT_CH = ["ch_internal", "ch_portal", "ch_email", "ch_sms", "ch_whatsapp", "ch_line", "ch_phone"];
  const channelsFor = scope === "lead" ? CHANNELS.filter(([k]) => LEAD_CH.includes(k))
    : scope === "client" ? CHANNELS.filter(([k]) => CLIENT_CH.includes(k)) : CHANNELS;

  const CH_TOP = scope === "client"
    ? ["ch_portal", "ch_email", "ch_sms", "ch_phone"]
    : scope === "lead"
    ? ["ch_internal", "ch_email", "ch_webchat"]
    : ["ch_internal", "ch_email", "ch_portal", "ch_webchat"];

  // CC-4: queue groupings for the redesigned sidebar
  const byQ = Object.fromEntries(QUEUES.map((q) => [q[0], q]));
  const nb = Object.fromEntries(NEW_BUSINESS.map((q) => [q[0], q]));
  const pick = (src, keys) => keys.map((k) => src[k]).filter(Boolean);
  const cc2Work = [
    ...(scope !== "client" ? pick(nb, ["new_inquiries", "needs_human"]) : []),
    ...pick(byQ, ["priority", "escalated"]),
    ...(scope !== "client" ? pick(nb, ["no_response"]) : []),
  ];
  const cc2Ownership = pick(byQ, ["mine", "team", "unassigned", "waiting_on_client"]);
  const cc2Closed = [...RESOLVED];
  const chTop = CH_TOP.map((k) => channelsFor.find(([ck]) => ck === k)).filter(Boolean);
  const chRest = channelsFor.filter(([ck]) => !CH_TOP.includes(ck));

  const loadCounts = useCallback(() => {
    const cParams = new URLSearchParams();
    if (scope) cParams.set("scope", scope);
    // For user/team perspectives, pass the filter so badge counts reflect the active perspective.
    // "all" and "unassigned" keep global counts intentionally.
    if (isMgmt && !scope && perspective.key !== "all" && perspective.key !== "unassigned") {
      Object.entries(perspective.params).forEach(([k, v]) => cParams.set(k, v));
    }
    api.get(`/communications/counts?${cParams}`).then((r) => setCounts(r.data)).catch(() => {});
  }, [scope, isMgmt, perspective]); // eslint-disable-line
  const loadList = useCallback(() => {
    // int_teams has a custom panel that fetches its own data
    if (queue.key === "int_teams") { setItems([]); return; }
    const params = new URLSearchParams();
    Object.entries(queue.params).forEach(([k, v]) => params.set(k, v));
    if (scope) params.set("scope", scope);
    if (q) params.set("q", q);
    // Team Inbox: when a specific team is picked, narrow to just that team's conversations.
    if (queue.key === "team" && teamFilter) params.set("assigned_team_id", teamFilter);
    // Perspective overlay — management only, full CC view only; non-management is server-enforced to "mine".
    if (isMgmt && !scope && perspective.key !== "all") {
      Object.entries(perspective.params).forEach(([k, v]) => params.set(k, v));
    }
    api.get(`/communications/inbox?${params}`).then((r) => {
      setItems(r.data.items);
      // Auto-select the first card ONLY when nothing is selected. Use a functional update so we read
      // the CURRENT activeId (not a stale closure) — otherwise a background reload would revert the
      // user's selection back to the first conversation in the list.
      setActiveId((cur) => cur || (r.data.items[0]?.id || null));
    }).catch(() => setItems([]));
  }, [queue, q, scope, teamFilter, isMgmt, perspective]); // eslint-disable-line
  useEffect(() => { loadCounts(); }, [loadCounts]);
  useEffect(() => { const id = setInterval(loadCounts, 15000); return () => clearInterval(id); }, [loadCounts]);
  useEffect(() => { const t = setTimeout(loadList, 200); return () => clearTimeout(t); }, [loadList]);
  useEffect(() => { api.get("/teams").then((r) => setTeams((r.data || []).filter((t) => t.active_flag !== false))).catch(() => {}); }, []);
  // UC-5A: load staff list for perspective switcher (management only; graceful 403 fallback for limited roles)
  useEffect(() => {
    if (isMgmt) {
      api.get("/users").then((r) => setStaffList((r.data || []).filter((u) => u.status === "active" && !u.is_deleted))).catch(() => {});
    }
  }, [isMgmt]); // eslint-disable-line

  // Deep-link: open a specific conversation from the header alert dropdown (?conversation=<id>).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => { const cid = searchParams.get("conversation"); if (cid) setActiveId(cid); }, [searchParams]);

  // UC-5B: Historical mode — ?historical_for=<clientId> activates a read-only historical lens.
  // URL is the entry-state authority; clearing the param restores the operational inbox.
  const historicalFor = searchParams.get("historical_for");
  const [historicalContactName, setHistoricalContactName] = useState(null);
  useEffect(() => {
    if (!historicalFor) {
      setHistoricalContactName(null);
      return;
    }
    setActiveId(null); // clear any stale operational conversation
    setQueue({ key: "historical_for", label: "Historical Emails", params: { historical_for_client_id: historicalFor } });
    // Fetch contact name for the banner
    api.get(`/communications/contacts/${historicalFor}/historical-count`)
      .then((r) => setHistoricalContactName(r.data.contact_name || historicalFor))
      .catch(() => setHistoricalContactName(null));
  }, [historicalFor]); // eslint-disable-line

  const exitHistoricalMode = () => {
    setSearchParams({});
    setQueue({ key: "new_inquiries", label: "New Inquiries", params: { inquiry: "new" } });
    setActiveId(null);
  };

  // URL-driven queue sync: when sidebar navigation sets ?qkey=..., sync the queue state here
  useEffect(() => {
    const qkey = searchParams.get("qkey");
    if (!qkey) return;
    const def = QKEY_MAP[qkey];
    if (def) {
      setQueue(prev => prev.key === def.key ? prev : { key: def.key, label: def.label, params: def.params });
    }
  }, [searchParams.get("qkey")]); // eslint-disable-line

  // Handle ?action=new_internal from sidebar "New Message" link
  useEffect(() => {
    if (searchParams.get("action") === "new_internal") {
      setNewInternalOpen(true);
      setSearchParams(prev => { const n = new URLSearchParams(prev); n.delete("action"); return n; });
    }
  }, [searchParams.get("action")]); // eslint-disable-line

  const changePerspective = (p) => { setPerspective(p); setActiveId(null); };
  const selectQueue = (key, label, params) => {
    setQueue({ key, label, params });
    setActiveId(null);
    // Sync URL so sidebar highlights the correct item + browser history/back works
    setSearchParams(sp => {
      const n = new URLSearchParams(sp);
      n.set("qkey", key);
      n.delete("conversation");
      n.delete("action");
      return n;
    });
  };

  const QueueItem = ({ qkey, label, icon, params, countKey, tone }) => {
    const Ic = Icons[icon] || Icons.Circle;
    const active = queue.key === qkey;
    const key = countKey ?? qkey;
    // cnt = membership count (always neutral — tells how many convs are in this view)
    const cnt = counts[key] || 0;
    // sa = staff-action count from the narrow responsibility projection.
    // Driven only by waiting_on_staff or (escalated + client/penny-last).
    // Waiting-on-client conversations NEVER contribute to sa, regardless of
    // priority level or escalation metadata.
    const sa = (counts.staff_action_buckets || {})[key] || 0;
    const pulse = sa > 0;
    // hasPartialAction: some members are staff-responsible but not all.
    // When true, show membership (cnt) neutrally + a separate rose badge for sa.
    // When false and pulse: all present members are staff-responsible — cnt badge goes rose.
    const hasPartialAction = pulse && sa < cnt;
    return (
      <button onClick={() => selectQueue(qkey, label, params)} data-testid={`cc-queue-${qkey}`}
        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${active ? "bg-blue-50 text-blue-700 font-medium" : pulse ? "text-rose-700 bg-rose-50/60 hover:bg-rose-50" : "text-slate-600 hover:bg-slate-50"}`}>
        <span className="relative flex items-center">
          <Ic className={`h-4 w-4 ${pulse ? "text-rose-500" : tone || (active ? "text-blue-600" : "text-slate-400")}`} />
          {pulse && <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-rose-500 animate-pulse ring-2 ring-rose-200" data-testid={`cc-pulse-${qkey}`} />}
        </span>
        <span className="flex-1 text-left truncate">{label}</span>
        {cnt > 0 && (
          <span data-testid={`cc-count-badge-${qkey}`}
            className={`text-xs min-w-[20px] text-center px-1.5 py-0.5 rounded-full font-semibold ${
              pulse && !hasPartialAction
                ? "bg-rose-500 text-white animate-pulse ring-2 ring-rose-300"
                : tone === "text-rose-500" ? "bg-rose-100 text-rose-700"
                : "bg-slate-100 text-slate-500"
            }`}>
            {cnt}
          </span>
        )}
        {hasPartialAction && (
          <span data-testid={`cc-unanswered-badge-${qkey}`}
            className="text-xs min-w-[20px] text-center px-1.5 py-0.5 rounded-full font-semibold bg-rose-500 text-white animate-pulse ring-2 ring-rose-300">
            {sa}
          </span>
        )}
      </button>
    );
  };
  const Section = ({ title, children }) => (
    <div className="mb-4"><div className="px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">{title}</div>{children}</div>
  );

  return (
    <div className="flex h-full min-h-0 bg-white" data-testid="communications-center">
      {/* PANEL 2 — Conversation List (Navigation is now in the primary TaxFlow sidebar) */}
      {queue.key === "identity_pending" ? (
        <IdentityReviewQueue />
      ) : (
        <>
      <section className="w-[340px] shrink-0 border-r border-slate-200 flex flex-col min-h-0" data-testid="cc-panel-list">
        {/* UC-5A: PERSPECTIVE SWITCHER — Management only, full Communications Center only */}
        {isMgmt && !scope && (
          <div className="px-3 py-2 border-b border-slate-100 shrink-0" data-testid="cc-perspective-bar">
            <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1">
              <Icons.Eye className="h-3 w-3" />Viewing
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="cc-perspective-trigger" className="w-full flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-lg px-2 py-1 text-xs text-indigo-700 hover:bg-indigo-100 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-200">
                  <Icons.Eye className="h-3 w-3 shrink-0" />
                  <span className="flex-1 text-left font-medium truncate" data-testid="cc-perspective-label">{queue.key?.startsWith("int_") ? queue.label : perspective.label}</span>
                  <Icons.ChevronDown className="h-3 w-3 shrink-0 opacity-60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64" data-testid="cc-perspective-menu">
                <DropdownMenuItem onClick={() => changePerspective({ key: "all", label: "All Communications", params: {} })} data-testid="cc-persp-all">
                  <Icons.Globe className="h-4 w-4 mr-2 text-slate-400" />All Communications
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changePerspective({ key: "mine", label: "My Communications", params: { assigned_user_id: me?.id } })} data-testid="cc-persp-mine">
                  <Icons.User className="h-4 w-4 mr-2 text-slate-400" />My Communications
                </DropdownMenuItem>
                {teams.length > 0 && (<>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-slate-400">Teams</DropdownMenuLabel>
                  {teams.map((t) => (
                    <DropdownMenuItem key={t.key} onClick={() => changePerspective({ key: `team_${t.key}`, label: t.name, params: { assigned_team_id: t.key } })} data-testid={`cc-persp-team-${t.key}`}>
                      <Icons.Users className="h-4 w-4 mr-2 text-slate-400" />{t.name}
                    </DropdownMenuItem>
                  ))}
                </>)}
                {staffList.length > 0 && (<>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-slate-400">Staff Members</DropdownMenuLabel>
                  {staffList.map((u) => (
                    <DropdownMenuItem key={u.id} onClick={() => changePerspective({ key: `user_${u.id}`, label: u.name || u.email, params: { assigned_user_id: u.id } })} data-testid={`cc-persp-user-${u.id}`}>
                      <Icons.UserCircle className="h-4 w-4 mr-2 text-slate-400" />{u.name || u.email}
                    </DropdownMenuItem>
                  ))}
                </>)}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => changePerspective({ key: "unassigned", label: "Unassigned", params: { view: "unassigned" } })} data-testid="cc-persp-unassigned">
                  <Icons.UserX className="h-4 w-4 mr-2 text-slate-400" />Unassigned
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
        {historicalFor && (
          <div className="px-3 py-2.5 bg-amber-50 border-b border-amber-200 flex items-center gap-2 shrink-0" data-testid="cc-historical-mode-banner">
            <Icons.Archive className="h-4 w-4 text-amber-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-amber-800">Historical Communications</div>
              {historicalContactName && <div className="text-[11px] text-amber-700 truncate">{historicalContactName}</div>}
            </div>
            <button onClick={exitHistoricalMode} data-testid="cc-historical-exit" className="text-xs text-amber-700 hover:text-amber-900 font-medium whitespace-nowrap inline-flex items-center gap-0.5">
              <Icons.ArrowLeft className="h-3 w-3" />Back
            </button>
          </div>
        )}
        <div className="px-3 py-3 border-b border-slate-100 flex items-center gap-2">
          <div className="relative flex-1">
            <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="cc-search" placeholder="Search conversations…" className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
          </div>
          {/* Compose / New message buttons */}
          {(queue.key === "ch_email" || queue.key.startsWith("ch_email_")) && (
            <button onClick={() => setPanelComposer({ mode: "new" })} data-testid="cc-compose-email-btn" title="Compose new email"
              className="shrink-0 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              <Icons.Pencil className="h-4 w-4" />
            </button>
          )}
          {(queue.key === "ch_internal" || queue.key === "int_my_messages") && (
            <button onClick={() => setNewInternalOpen(true)} data-testid="cc-new-internal-btn" title="New internal message"
              className="shrink-0 p-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors">
              <Icons.Plus className="h-4 w-4" />
            </button>
          )}
          {queue.key === "int_announcements" && isMgmt && (
            <button onClick={() => setNewInternalOpen(true)} data-testid="cc-new-announcement-btn" title="New firm announcement"
              className="shrink-0 p-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors">
              <Icons.Megaphone className="h-4 w-4" />
            </button>
          )}
        </div>
        {queue.key === "team" && (
          <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2 bg-slate-50/60" data-testid="cc-team-picker-row">
            <Icons.Users className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            <select
              data-testid="cc-team-picker"
              value={teamFilter}
              onChange={(e) => { setTeamFilter(e.target.value); setActiveId(null); }}
              className="flex-1 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-200">
              <option value="">All Teams</option>
              {teams.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
            </select>
          </div>
        )}
        {/* Inactive Client Bulk Archive toolbar */}
        {queue.key === "inactive_clients" && isMgmt && (
          <div className="px-3 py-2 border-b border-amber-200 bg-amber-50 flex items-center gap-2 shrink-0" data-testid="cc-inactive-clients-toolbar">
            <Icons.UserMinus className="h-3.5 w-3.5 text-amber-600 shrink-0" />
            <span className="text-xs text-amber-800 font-medium flex-1">Conversations from inactive clients</span>
            <button
              onClick={() => {
                setBulkSelected(new Set());
                setArchiveCandidates(null);
                setBulkArchiveOpen(true);
                api.get("/communications/conversations/inactive-archive-candidates")
                  .then(r => setArchiveCandidates(r.data))
                  .catch(() => setArchiveCandidates({ candidates: [], total: 0, eligible_count: 0 }));
              }}
              data-testid="cc-bulk-archive-open-btn"
              className="text-xs bg-amber-700 hover:bg-amber-800 text-white rounded-lg px-3 py-1.5 font-medium transition-colors whitespace-nowrap"
            >
              Review &amp; Archive
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto tf-scroll" data-testid="cc-conversation-list">
          {queue.key === "int_teams" ? (
            <InternalTeamsPanel onSelectTeamRoom={(cid) => setActiveId(cid)} />
          ) : items === null ? <div className="py-12 text-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500 inline" /></div> :
          items.length === 0 ? <div className="py-12 text-center text-slate-400 text-sm">
            {historicalFor ? "No historical emails found for this contact." : `No conversations in ${queue.label}.`}
          </div> :
          items.map((c) => <ConversationCard key={c.id} c={c} active={activeId === c.id} onClick={() => setActiveId(c.id)} />)}
        </div>
      </section>

      {/* PANEL 3 — Conversation Workspace */}
      <section className="flex-1 min-w-0 flex flex-col bg-slate-50/40 min-h-0 overflow-hidden" data-testid="cc-panel-workspace">
        {activeId ? <Workspace key={activeId} cid={activeId} me={me} isMgmt={isMgmt} onChanged={() => { loadList(); loadCounts(); }} onClose={() => setActiveId(null)} navigate={navigate} />
          : <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
              {historicalFor ? "Select a historical email to read." : "Select a conversation to begin."}
            </div>}
      </section>
        </>
      )}

      {showInbound && <InboundInquiryDialog onClose={() => setShowInbound(false)} onDone={(cid) => { setShowInbound(false); selectQueue("awaiting_first_response", "Awaiting First Response", { inquiry: "awaiting_first_response" }); loadCounts(); if (cid) setTimeout(() => setActiveId(cid), 300); }} />}

      {/* Stage 3: New Internal Thread dialog */}
      {newInternalOpen && (
        <NewInternalDialog
          onClose={() => setNewInternalOpen(false)}
          onDone={(cid) => {
            setNewInternalOpen(false);
            loadList();
            if (cid) setTimeout(() => setActiveId(cid), 300);
          }}
        />
      )}

      {/* Stage 3: Panel-level email composer (for "New Email" from Email channel) */}
      {panelComposer && (
        <ComposerModal
          mode={panelComposer.mode}
          conversation={panelComposer.conversation}
          parentMessage={panelComposer.parentMessage}
          onClose={() => setPanelComposer(null)}
          onSent={() => { setPanelComposer(null); loadList(); loadCounts(); }}
        />
      )}

      {/* Inactive Client Bulk Archive Dialog */}
      {bulkArchiveOpen && (
        <BulkArchiveDialog
          candidates={archiveCandidates}
          selected={bulkSelected}
          onToggle={(id) => setBulkSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; })}
          onSelectEligible={() => {
            if (!archiveCandidates) return;
            setBulkSelected(new Set(archiveCandidates.candidates.filter(c => c.is_eligible).map(c => c.id)));
          }}
          onClose={() => { setBulkArchiveOpen(false); setBulkSelected(new Set()); setArchiveCandidates(null); }}
          onArchived={() => { setBulkArchiveOpen(false); setBulkSelected(new Set()); setArchiveCandidates(null); loadList(); loadCounts(); }}
        />
      )}
    </div>
  );
}

function InboundInquiryDialog({ onClose, onDone }) {
  const [form, setForm] = useState({ contact_name: "", body: "", channel: "webchat", priority: "normal" });
  const [busy, setBusy] = useState(false);
  const cls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm";
  const submit = async () => {
    if (!form.contact_name.trim() || !form.body.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/communications/inquiries/inbound", form);
      toast.success("Inquiry received — auto-acknowledged & routed");
      onDone(data.conversation_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="cc-inbound-dialog"><DialogHeader><DialogTitle>Log Inbound Inquiry</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input className={cls} placeholder="Contact name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="cc-inbound-name" />
          <textarea className={cls} rows={3} placeholder="Their message…" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="cc-inbound-body" />
          <div className="grid grid-cols-2 gap-3">
            <select className={cls} value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} data-testid="cc-inbound-channel">
              {["webchat", "website_form", "google_business", "email", "whatsapp", "referral"].map((c) => <option key={c} value={c}>{ch(c).label}</option>)}
            </select>
            <select className={cls} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option></select>
          </div>
          <p className="text-xs text-slate-400">Penny will auto-acknowledge (business-hours aware), route to the right team, and start the human-response SLA timer.</p>
        </div>
        <DialogFooter><button onClick={submit} disabled={busy} data-testid="cc-inbound-submit" className="bg-orange-600 hover:bg-orange-700 text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50">{busy ? "Logging…" : "Log Inquiry"}</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// New Internal Message / Firm Announcement Dialog
function NewInternalDialog({ onClose, onDone }) {
  const { user } = useAuth();
  const isMgmt = ["administrator", "partner", "manager"].includes(user?.role);
  const [form, setForm] = useState({ subject: "", body: "", internal_type: "private", recipient_ids: [] });
  const [staffList, setStaffList] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const cls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200";

  useEffect(() => {
    api.get("/users").then(r => setStaffList((r.data || []).filter(u => u.status === "active" && !u.is_deleted && u.id !== user?.id))).catch(() => {});
  }, [user?.id]); // eslint-disable-line

  const uploadFile = async (file) => {
    if (!file) return;
    setUploadingAttachment(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/communications/internal/attachment-draft", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachments(prev => [...prev, { id: data.id, filename: data.filename }]);
    } catch (e) { toast.error(e.response?.data?.detail || "File upload failed"); }
    finally { setUploadingAttachment(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const toggleRecipient = (uid) => setForm(f => ({
    ...f,
    recipient_ids: f.recipient_ids.includes(uid) ? f.recipient_ids.filter(id => id !== uid) : [...f.recipient_ids, uid]
  }));

  const submit = async () => {
    if (!form.subject.trim() || !form.body.trim()) return;
    setBusy(true);
    try {
      let cid;
      if (form.internal_type === "announcement") {
        const { data } = await api.post("/communications/internal/announcements", {
          subject: form.subject, body: form.body, attachment_ids: attachments.map(a => a.id),
        });
        cid = data.conversation?.id;
        toast.success("Firm Announcement published");
      } else {
        const { data } = await api.post("/communications/internal/conversations", {
          subject: form.subject, body: form.body,
          recipient_ids: form.recipient_ids, internal_type: "private", priority: "normal",
        });
        cid = data.conversation?.id;
        toast.success("Internal message thread created");
      }
      onDone(cid);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create internal thread"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="cc-new-internal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icons.Lock className="h-4 w-4 text-violet-600" />
            {form.internal_type === "announcement" ? "New Firm Announcement" : "New Internal Message"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {isMgmt && (
            <div className="flex gap-2">
              <button onClick={() => setForm(f => ({...f, internal_type: "private"}))} data-testid="cc-internal-type-dm"
                className={`flex-1 py-2 text-sm rounded-lg border transition-colors font-medium ${form.internal_type === "private" ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200 hover:border-violet-300"}`}>
                <Icons.MessageCircle className="h-3.5 w-3.5 inline mr-1.5" />Direct Message
              </button>
              <button onClick={() => setForm(f => ({...f, internal_type: "announcement", recipient_ids: []}))} data-testid="cc-internal-type-announcement"
                className={`flex-1 py-2 text-sm rounded-lg border transition-colors font-medium ${form.internal_type === "announcement" ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200 hover:border-violet-300"}`}>
                <Icons.Megaphone className="h-3.5 w-3.5 inline mr-1.5" />Firm Announcement
              </button>
            </div>
          )}
          {form.internal_type !== "announcement" && staffList.length > 0 && (
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">
                <Icons.MessageCircle className="h-3.5 w-3.5 inline mr-1 text-violet-500" />
                Direct Message Recipients <span className="text-slate-400">(optional — creates group thread)</span>
              </label>
              <div className="border border-slate-200 rounded-lg max-h-32 overflow-y-auto" data-testid="cc-internal-recipient-list">
                {staffList.map(u => (
                  <label key={u.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                    <input type="checkbox" checked={form.recipient_ids.includes(u.id)} onChange={() => toggleRecipient(u.id)}
                      className="rounded text-violet-600 border-slate-300 focus:ring-violet-200" data-testid={`cc-internal-recip-${u.id}`} />
                    <span className="text-sm text-slate-700">{u.name || u.email}</span>
                    <span className="text-[10px] text-slate-400 ml-auto capitalize">{u.role}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <input className={cls} placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="cc-internal-subject" />
          <textarea className={cls} rows={4}
            placeholder={form.internal_type === "announcement" ? "Write your firm announcement…" : "Write your internal message…"}
            value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="cc-internal-body" />
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {attachments.map(a => (
                <div key={a.id} className="flex items-center gap-1 bg-violet-50 border border-violet-200 rounded-lg px-2 py-1 text-xs text-violet-700">
                  <Icons.Paperclip className="h-3 w-3 shrink-0" />
                  <span className="max-w-[140px] truncate">{a.filename}</span>
                  <button onClick={() => setAttachments(prev => prev.filter(x => x.id !== a.id))} className="text-violet-400 hover:text-violet-700 ml-0.5"><Icons.X className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2">
            <label htmlFor="internal-attach-new" className="cursor-pointer inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-violet-600 transition-colors px-2 py-1 rounded-lg hover:bg-violet-50">
              {uploadingAttachment ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icons.Paperclip className="h-3.5 w-3.5" />}Attach file
            </label>
            <input id="internal-attach-new" ref={fileRef} type="file" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])} />
            <span className="text-xs text-slate-400 flex-1 text-right">Staff only — never client-visible</span>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg">Cancel</button>
          <button onClick={submit} disabled={busy || !form.subject.trim() || !form.body.trim()} data-testid="cc-internal-submit"
            className="bg-violet-600 hover:bg-violet-700 text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50">
            {busy ? "Sending…" : form.internal_type === "announcement" ? "Publish Announcement" : "Send Message"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Inactive Client Bulk Archive Dialog
function BulkArchiveDialog({ candidates, selected, onToggle, onSelectEligible, onClose, onArchived }) {
  const [step, setStep] = useState("review"); // review | confirm | done
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const cls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200";

  const selectedArr = Array.from(selected);
  const eligibleCount = candidates?.eligible_count ?? 0;
  const ineligibleSelected = selectedArr.filter(id => {
    const c = (candidates?.candidates || []).find(x => x.id === id);
    return c && !c.is_eligible;
  });

  const handleArchive = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/communications/conversations/bulk-archive", {
        conversation_ids: selectedArr,
        confirmation_count: selectedArr.length,
      });
      setResult(data);
      setStep("done");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="cc-bulk-archive-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
          <div className="p-2 bg-amber-100 rounded-lg shrink-0"><Icons.Archive className="h-5 w-5 text-amber-700" /></div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900">Inactive Client Conversation Review</h3>
            <p className="text-xs text-slate-500">Select eligible conversations for bulk archival. CRM records, correspondence, and audit history are preserved.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 shrink-0"><Icons.X className="h-5 w-5" /></button>
        </div>

        {step === "done" ? (
          <div className="p-6 flex-1 flex flex-col items-center justify-center gap-4">
            <div className="p-3 bg-emerald-100 rounded-full"><Icons.CheckCircle2 className="h-8 w-8 text-emerald-600" /></div>
            <div className="text-center">
              <div className="text-lg font-semibold text-slate-900">Archive complete</div>
              <div className="text-sm text-slate-600 mt-1">
                {result?.archived_count} conversation{result?.archived_count !== 1 ? "s" : ""} archived.
                {result?.skipped_count > 0 && ` ${result.skipped_count} skipped.`}
              </div>
            </div>
            <button onClick={onArchived} className="bg-slate-800 text-white rounded-lg px-6 py-2 text-sm font-medium" data-testid="cc-bulk-archive-done-btn">Done</button>
          </div>
        ) : step === "confirm" ? (
          <div className="p-6 flex-1 flex flex-col gap-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-start gap-3">
                <Icons.AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-amber-900">Confirm bulk archival</div>
                  <p className="text-sm text-amber-800 mt-1">
                    You are about to archive <strong>{selectedArr.length}</strong> conversation{selectedArr.length !== 1 ? "s" : ""} from inactive clients.
                    This action removes them from active queues. They will remain accessible via the Archived view.
                    CRM linkage, correspondence, and audit history are fully preserved.
                  </p>
                  {ineligibleSelected.length > 0 && (
                    <p className="text-xs text-rose-700 mt-2 font-medium">
                      Warning: {ineligibleSelected.length} selected conversation{ineligibleSelected.length !== 1 ? "s have" : " has"} blocking conditions. These will be skipped during archival.
                    </p>
                  )}
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-500">Note: This does not move emails to Titan Trash and does not change any client relationship status.</p>
            <div className="flex gap-3 mt-auto">
              <button onClick={() => setStep("review")} className="flex-1 text-sm text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg px-4 py-2.5 transition-colors" data-testid="cc-bulk-archive-back-btn">Back</button>
              <button onClick={handleArchive} disabled={busy} data-testid="cc-bulk-archive-confirm-btn"
                className="flex-1 text-sm font-medium text-white bg-amber-700 hover:bg-amber-800 disabled:opacity-60 rounded-lg px-4 py-2.5 transition-colors inline-flex items-center justify-center gap-2">
                {busy ? <><Icons.Loader2 className="h-4 w-4 animate-spin" />Archiving…</> : <><Icons.Archive className="h-4 w-4" />Archive {selectedArr.length} conversation{selectedArr.length !== 1 ? "s" : ""}</>}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Summary bar */}
            <div className="px-6 py-3 border-b border-slate-100 bg-slate-50 flex items-center gap-4 flex-wrap shrink-0">
              {candidates === null ? (
                <span className="text-sm text-slate-500 flex items-center gap-1"><Icons.Loader2 className="h-4 w-4 animate-spin" />Loading candidates…</span>
              ) : (
                <>
                  <span className="text-sm text-slate-700">{candidates.total} conversation{candidates.total !== 1 ? "s" : ""} from inactive clients</span>
                  <span className="text-sm text-emerald-700 font-medium"><Icons.CheckCircle2 className="h-3.5 w-3.5 inline mr-1" />{eligibleCount} eligible for archival</span>
                  {selected.size > 0 && <span className="text-sm font-semibold text-amber-700">{selected.size} selected</span>}
                  <div className="ml-auto flex gap-2">
                    <button onClick={onSelectEligible} data-testid="cc-bulk-select-eligible-btn"
                      className="text-xs bg-emerald-100 text-emerald-800 hover:bg-emerald-200 rounded-lg px-3 py-1.5 font-medium transition-colors">
                      Select all eligible ({eligibleCount})
                    </button>
                    {selected.size > 0 && (
                      <button onClick={() => { /* clear handled by parent */ onSelectEligible(); }} className="text-xs text-slate-500 hover:text-slate-700 px-2">Clear</button>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Candidate list */}
            <div className="flex-1 overflow-y-auto tf-scroll min-h-0" data-testid="cc-bulk-archive-list">
              {candidates?.candidates?.map(c => (
                <label key={c.id} htmlFor={`ba-${c.id}`} data-testid={`cc-bulk-item-${c.id}`}
                  className={`flex items-start gap-3 px-6 py-3 border-b border-slate-100 cursor-pointer transition-colors ${selected.has(c.id) ? "bg-amber-50" : "hover:bg-slate-50"}`}>
                  <input id={`ba-${c.id}`} type="checkbox" checked={selected.has(c.id)} onChange={() => onToggle(c.id)}
                    className="mt-1 rounded border-slate-300 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-slate-900 truncate">{c.client_name}</span>
                      {c.client_number && <span className="text-[10px] text-slate-400">{c.client_number}</span>}
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ml-auto ${c.is_eligible ? "bg-emerald-100 text-emerald-700" : "bg-rose-50 text-rose-600"}`}>
                        {c.is_eligible ? "Eligible" : "Blocked"}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 truncate mt-0.5">{c.subject || "No subject"} · {c.channel} · {c.conv_status}</div>
                    {!c.is_eligible && c.blocking_conditions?.map(b => (
                      <div key={b.code} className="text-[10px] text-rose-600 flex items-center gap-1 mt-0.5">
                        <Icons.AlertCircle className="h-3 w-3 shrink-0" />{b.reason}
                      </div>
                    ))}
                  </div>
                </label>
              ))}
              {candidates?.candidates?.length === 0 && (
                <div className="py-12 text-center text-slate-400 text-sm">No inactive client conversations found in active queues.</div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-100 flex gap-3 shrink-0">
              <button onClick={onClose} className="flex-1 text-sm text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg px-4 py-2.5 transition-colors" data-testid="cc-bulk-archive-cancel-btn">Cancel</button>
              <button onClick={() => setStep("confirm")} disabled={selected.size === 0} data-testid="cc-bulk-archive-review-btn"
                className="flex-1 text-sm font-medium text-white bg-amber-700 hover:bg-amber-800 disabled:opacity-40 rounded-lg px-4 py-2.5 transition-colors">
                Review {selected.size > 0 ? `(${selected.size})` : ""} &amp; Archive
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// A3: Block Sender Modal
function BlockSenderModal({ conv, sending, onConfirm, onClose }) {
  const [reason, setReason] = useState("spam");
  const [notes, setNotes] = useState("");
  const [confirmClient, setConfirmClient] = useState(false);
  const [warning, setWarning] = useState(null);
  const senderEmail = conv.contact_email || conv.contact_name || "this sender";
  const isBlocked = conv.is_email_blocked;

  const handleSubmit = async () => {
    const res = await onConfirm({ reason, notes, confirm: confirmClient });
    if (res?.warning) setWarning(res);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="cc-block-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-rose-100 rounded-lg"><Icons.Ban className="h-5 w-5 text-rose-600" /></div>
          <div>
            <h3 className="font-semibold text-slate-900">Block Sender & Archive</h3>
            <p className="text-xs text-slate-500">{senderEmail}</p>
          </div>
          <button onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-600"><Icons.X className="h-5 w-5" /></button>
        </div>

        {isBlocked ? (
          <div className="p-3 bg-rose-50 rounded-lg text-sm text-rose-700 mb-4"><Icons.Ban className="h-4 w-4 inline mr-1.5" />This sender is already blocked.</div>
        ) : (
          <>
            {warning && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 mb-4">
                <strong>Warning:</strong> {warning.message}
                <label className="flex items-center gap-2 mt-2 cursor-pointer">
                  <input type="checkbox" checked={confirmClient} onChange={e => setConfirmClient(e.target.checked)} className="rounded" data-testid="cc-block-confirm-check" />
                  <span>Yes, I understand — block this {warning.associated_type} anyway</span>
                </label>
              </div>
            )}
            <div className="space-y-4 mb-4">
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1.5">Reason</label>
                <div className="flex flex-wrap gap-2" data-testid="cc-block-reason">
                  {["spam", "marketing", "harassment", "unwanted", "manual"].map(r => (
                    <button key={r} onClick={() => setReason(r)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${reason === r ? "bg-rose-600 text-white border-rose-600" : "border-slate-200 text-slate-600 hover:border-rose-300"}`}>
                      {r.charAt(0).toUpperCase() + r.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1.5">Notes (optional)</label>
                <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Additional context for audit log…"
                  className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-300 resize-none" data-testid="cc-block-notes" />
              </div>
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 mb-4">
              <strong>Important:</strong> Blocking within TaxFlow quarantines all conversations from this sender across the three operational mailboxes. <strong>This does not reject mail at the Titan/Hostinger email server.</strong> The sender can still send email to the firm's inboxes; future messages will be automatically quarantined.
            </div>
            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg px-4 py-2.5 transition-colors">Cancel</button>
              <button onClick={handleSubmit} disabled={sending || (warning && !confirmClient)}
                data-testid="cc-block-confirm-btn"
                className="flex-1 text-sm font-medium text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-60 rounded-lg px-4 py-2.5 transition-colors inline-flex items-center justify-center gap-2">
                {sending ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Ban className="h-4 w-4" />}
                Block & Archive
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ConversationCard({ c, active, onClick }) {
  const w = WAITING[c.conv_status] || WAITING.open;
  const cm = ch(c.channel);
  const isHistorical = c.is_historical_import === true;
  return (
    <button onClick={onClick} data-testid={`cc-card-${c.id}`}
      className={`w-full text-left px-3.5 py-3 border-b border-slate-100 transition-colors ${active ? "bg-blue-50/70 border-l-2 border-l-blue-600" : "hover:bg-slate-50"}`}>
      {isHistorical && (
        <div className="flex items-center gap-1 mb-1.5" data-testid={`cc-card-historical-badge-${c.id}`}>
          <span className="text-[9px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5">
            <Icons.Archive className="h-2.5 w-2.5" />Historical · Read only
          </span>
        </div>
      )}
      {c.is_trashed && (
        <div className="flex items-center gap-1 mb-1.5" data-testid={`cc-card-trash-badge-${c.id}`}>
          <span className="text-[9px] font-bold uppercase tracking-wide bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5">
            <Icons.Trash2 className="h-2.5 w-2.5" />Trash
          </span>
        </div>
      )}
      {c.lifecycle_status === "inactive_client" && !c.is_trashed && !c.is_archived && (
        <div className="flex items-center gap-1 mb-1.5" data-testid={`cc-card-inactive-badge-${c.id}`}>
          <span className="text-[9px] font-bold uppercase tracking-wide bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5">
            <Icons.UserMinus className="h-2.5 w-2.5" />Inactive Client
          </span>
        </div>
      )}
      <div className="flex items-start gap-3">
        <div className="relative">
          {c.unread_count > 0 && <span className="absolute -left-1 top-1 h-2 w-2 rounded-full bg-blue-600" />}
          <Avatar name={c.client_name} url={c.client_photo_url} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold text-slate-900 truncate">{c.client_name || "Unknown"}</span>
            <span className="text-[11px] text-slate-400 shrink-0">{timeAgo(c.last_message_at)}</span>
          </div>
          {c.is_inquiry ? (
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              <span className="text-[9px] font-bold uppercase tracking-wide bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded" data-testid={`cc-inquiry-badge-${c.id}`}>New Inquiry</span>
              <span className="text-[10px] text-slate-500">{ch(c.inquiry_source).label}</span>
              {c.inquiry_status === "needs_human" && <span className="text-[10px] text-rose-600 flex items-center gap-0.5"><Icons.UserCheck className="h-3 w-3" />Needs Human</span>}
              {c.irp_sla_state && c.irp_sla_state !== "pending" && (
                <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded inline-flex items-center gap-0.5 ${IRP_TONE[c.irp_sla_state]} ${c.irp_sla_state === "breached" ? "animate-pulse" : ""}`} data-testid={`cc-irp-sla-${c.id}`}>
                  <Icons.Clock className="h-2.5 w-2.5" />{IRP_LABEL[c.irp_sla_state]}{c.irp_sla_state !== "answered" && c.irp_minutes_remaining != null ? ` · ${fmtRemaining(c.irp_minutes_remaining)}` : ""}
                </span>
              )}
              {c.irp_commander_name && <span className="text-[9px] text-blue-600 inline-flex items-center gap-0.5" data-testid={`cc-commander-${c.id}`}><Icons.ShieldCheck className="h-2.5 w-2.5" />{c.irp_commander_name}</span>}
            </div>
          ) : c.channel === "email" && c.correspondent_type && (c.correspondent_type !== "spam" || c.is_email_blocked || c.is_spam) ? (
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap" data-testid={`cc-email-type-${c.id}`}>
              {ctBadge(c.correspondent_type, true)}
              {c.is_email_blocked && <span className="text-[9px] font-bold bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5"><Icons.Ban className="h-2.5 w-2.5" />Blocked</span>}
            </div>
          ) : (
            <div className="text-sm text-blue-600 font-medium truncate">{c.engagement_number ? `${c.engagement_number}` : (c.subject || "General")}</div>
          )}
          <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5">
            <span>{c.department || "—"}</span><Icons.ChevronRight className="h-3 w-3 text-slate-300" /><span className={w[1]}>{c.is_inquiry ? (c.penny_status || "Idle") : w[0]}</span>
          </div>
          {c.assigned_team_name && (
            <div className="mt-1">
              <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-100" data-testid={`cc-card-team-${c.id}`}>
                <Icons.Users className="h-2.5 w-2.5" />{c.assigned_team_name}{!c.assigned_user_name && " · unclaimed"}
              </span>
            </div>
          )}
          {(c.is_inquiry ? c.lead_score != null : c.assigned_user_name) && (
            <div className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">{c.is_inquiry ? <>Lead score: <span className="font-semibold text-slate-700">{c.lead_score}</span></> : <>Assigned: <Avatar name={c.assigned_user_name} size="h-4 w-4" color="bg-slate-400" /> {c.assigned_user_name}</>}</div>
          )}
          <div className="flex items-center justify-between gap-2 mt-2">
            <span className="text-xs text-slate-500 truncate flex-1">{c.last_message || "—"}</span>
            <div className="flex items-center gap-1 shrink-0">
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${cm.tone}`}>{cm.label}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded capitalize ${PRIORITY_TONE[c.priority] || PRIORITY_TONE.normal}`}>{c.priority}</span>
              {c.unread_count > 0 && <span className="text-[10px] min-w-[16px] text-center px-1 py-0.5 rounded-full bg-rose-500 text-white font-semibold">{c.unread_count}</span>}
            </div>
          </div>
          {c.portal_activated && (
            <div className="mt-2" data-testid={`cc-portal-activated-${c.id}`}>
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                <Icons.ShieldCheck className="h-3 w-3" />
                Portal: Activated{c.portal_activated_source ? ` (from ${c.portal_activated_source})` : ""}
              </span>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

const HEADER_TONE = { cleared: "text-emerald-600", balance_due: "text-rose-600", none: "text-slate-500", complete: "text-emerald-600" };

// Simple markdown → safe HTML converter for Allison's advisory output.
// Supports: ## headings, **bold**, *italic*, - bullet lists, numbered lists, blank-line paragraphs.
function mdToHtml(md = "") {
  if (!md) return "";
  const lines = md.split("\n");
  const out = [];
  let inList = false;
  for (const line of lines) {
    const esc = line.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const fmt = (s) => s
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>");
    if (/^### (.+)$/.test(esc)) {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<div style="font-weight:700;margin:8px 0 3px;font-size:0.68rem;text-transform:uppercase;letter-spacing:0.07em;color:#64748b">${fmt(esc.replace(/^### /, ""))}</div>`);
    } else if (/^## (.+)$/.test(esc)) {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<div style="font-weight:700;margin:10px 0 3px;font-size:0.78rem;color:#1e293b">${fmt(esc.replace(/^## /, ""))}</div>`);
    } else if (/^# (.+)$/.test(esc)) {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<div style="font-weight:700;margin:10px 0 4px;font-size:0.82rem;color:#1e293b">${fmt(esc.replace(/^# /, ""))}</div>`);
    } else if (/^[-*] (.+)$/.test(esc)) {
      if (!inList) { out.push('<ul style="list-style:disc;padding-left:14px;margin:4px 0">'); inList = true; }
      out.push(`<li style="margin:2px 0">${fmt(esc.replace(/^[-*] /, ""))}</li>`);
    } else if (/^\d+\. (.+)$/.test(esc)) {
      if (!inList) { out.push('<ul style="list-style:decimal;padding-left:14px;margin:4px 0">'); inList = true; }
      out.push(`<li style="margin:2px 0">${fmt(esc.replace(/^\d+\. /, ""))}</li>`);
    } else if (esc.trim() === "") {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push('<div style="margin:4px 0"></div>');
    } else {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<div style="margin:2px 0">${fmt(esc)}</div>`);
    }
  }
  if (inList) out.push("</ul>");
  return out.join("");
}

function Workspace({ cid, me, isMgmt, onChanged, onClose, navigate }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("conversation");
  const [reply, setReply] = useState("");
  const [noteMode, setNoteMode] = useState(false);
  const [sending, setSending] = useState(false);
  const [showSendMenu, setShowSendMenu] = useState(false);
  // Close the send dropdown when clicking outside — use mousedown + ref so the
  // dropdown item's onClick fires BEFORE the menu is hidden (capture-phase close
  // was unmounting the item before its handler could execute).
  useEffect(() => {
    if (!showSendMenu) return;
    const handler = (e) => {
      if (sendMenuRef.current && !sendMenuRef.current.contains(e.target)) {
        setShowSendMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showSendMenu]);
  const [composerExpanded, setComposerExpanded] = useState(false); // Stage 3: expand/collapse
  const [dialog, setDialog] = useState(null); // {type}
  const [tpls, setTpls] = useState([]);
  const [showTpl, setShowTpl] = useState(false);
  const [teams, setTeams] = useState([]);
  const [consulting, setConsulting] = useState(false);
  const [railOpen, setRailOpen] = useState(true); // CC-1 Context rail (local UI state only)
  const [allison, setAllison] = useState(null); // CC-3: latest endpoint-backed Allison briefing
  const [blockModal, setBlockModal] = useState(false); // A3: block sender modal
  const [emailAdvisory, setEmailAdvisory] = useState(null); // A2: Allison email classification advisory
  // Stage 3: rich-text email composer state
  const [composer, setComposer] = useState(null); // {mode, parentMessage} or null
  const [allisonTab, setAllisonTab] = useState("allison"); // "allison" | "case"
  const [histCount, setHistCount] = useState(null);
  const [histError, setHistError] = useState(false);
  // Allison Quick Review
  const [quickReview, setQuickReview] = useState(null);
  const [quickReviewLoading, setQuickReviewLoading] = useState(false);
  // Convert to Lead workflow
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [convertResult, setConvertResult] = useState(null); // post-conversion status
  const [trashConvDialog, setTrashConvDialog] = useState(false); // Trash conversation confirmation
  const [trashMsgDialog, setTrashMsgDialog] = useState(null); // {message} — single message trash confirmation
  // Internal channel attachments (for DMs, team rooms, announcements)
  const [internalAttachments, setInternalAttachments] = useState([]);
  const [uploadingInternalAttachment, setUploadingInternalAttachment] = useState(false);
  const internalFileRef = useRef(null);
  const bottomRef = useRef(null);
  const composerRef = useRef(null);
  const focusedFor = useRef(null);
  const sendMenuRef = useRef(null);
  // DRL-2B: read ephemeral draft from navigation state (prepare-communication endpoint).
  // Applied once per conversation navigation — cleared after use so re-entry doesn't re-apply.
  const { state: navState } = useLocation();

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/communications/conversations/${cid}`);
      setData(r.data);
      if (focusedFor.current !== cid) {
        // Auto-initialize email composer once per conversation navigation
        const conv0 = r.data?.conversation;
        if (conv0?.channel === "email" && !conv0?.is_historical_import && !conv0?.is_email_blocked) {
          const lastIn = [...(r.data.messages||[])].reverse().find(m=>m.direction==="inbound");
          setComposer({ mode: "reply", parentMessage: lastIn || r.data.messages?.slice(-1)[0] || null });
        } else {
          setComposer(null);
        }
        focusedFor.current = cid;
        // wait for the thread + composer to paint, then jump to newest message and focus the reply box
        requestAnimationFrame(() => requestAnimationFrame(() => {
          bottomRef.current?.scrollIntoView();
          if (!noteMode) composerRef.current?.focus();
        }));
      }
    } catch { /* ignore */ }
  }, [cid]); // eslint-disable-line
  useEffect(() => { (async () => { await load(); onChanged?.(); })(); }, [cid]); // eslint-disable-line

  // DRL-2B: prefill the composer with the Allison-prepared draft when navigated here via
  // Prepare Draft. The initialDraft is in React Router location state (ephemeral — not persisted).
  // Applied once per cid change to avoid re-applying on subsequent reloads.
  useEffect(() => {
    if (navState?.initialDraft && focusedFor.current === cid) {
      setReply(navState.initialDraft);
      setNoteMode(false);
      // Focus the composer so the staff can immediately review/edit
      requestAnimationFrame(() => composerRef.current?.focus());
    }
  }, [cid]); // eslint-disable-line
  useEffect(() => { api.get("/communications/templates").then((r) => setTpls(r.data.items || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/teams").then((r) => setTeams((r.data || []).filter((t) => t.active_flag !== false))).catch(() => {}); }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView(); }, [data?.messages?.length]);
  // Combined Allison/Case panel: fetch historical-count for Case tab
  useEffect(() => {
    const clientId = data?.client?.id;
    setHistCount(null); setHistError(false);
    if (!clientId) return;
    api.get(`/communications/contacts/${clientId}/historical-count`).then((r) => setHistCount(r.data.count)).catch(() => setHistError(true));
  }, [data?.client?.id]); // eslint-disable-line
  // CC-3: surface the latest existing Allison briefing (from /consult-allison) without re-running.
  useEffect(() => {
    const notes = (data?.messages || []).filter((m) => m.sender_type === "allison");
    if (notes.length) {
      const last = notes[notes.length - 1];
      const body = (last.body || "").replace(/^🧠\s*\*\*Allison[^\n]*\*\*\s*/i, "").trim();
      setAllison({ text: body, at: last.sent_at, source: "saved" });
    } else { setAllison(null); }
  }, [cid, data?.messages?.length]); // eslint-disable-line

  // Allison Quick Review: fetch when conversation changes (fast, CRM-driven, no LLM)
  useEffect(() => {
    if (!cid) return;
    setQuickReview(null);
    setConvertResult(null);
    setQuickReviewLoading(true);
    api.get(`/communications/conversations/${cid}/allison-quick-review`)
      .then(r => setQuickReview(r.data))
      .catch(() => setQuickReview(null))
      .finally(() => setQuickReviewLoading(false));
  }, [cid]); // eslint-disable-line

  if (!data) return <div className="flex-1 flex items-center justify-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500" /></div>;
  const { conversation: conv, client, messages, context: ctx, intake } = data;
  const isInquiry = !!intake;
  const isHistorical = conv.is_historical_import === true;
  // Internal conversations: use the creator name; external: use CRM client or contact
  const isInternal = conv.channel === "internal";
  const displayName = isInternal
    ? (conv.created_by_name || "Staff")
    : (client?.full_name || conv.contact_name || "Visitor");

  const assignToTeam = async (team) => {
    setSending(true);
    try {
      await api.post(`/communications/conversations/${cid}/assign`, { assigned_team_id: team.key });
      toast.success(`Assigned to ${team.name}`);
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Assign failed"); } finally { setSending(false); }
  };

  const claimLead = async () => {
    setSending(true);
    try {
      await api.post(`/communications/conversations/${cid}/claim`);
      toast.success("You claimed this lead — it's now in your inbox");
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not claim this lead"); } finally { setSending(false); }
  };

  const goToNextAction = () => {
    const link = data?.intake?.next_action_link;
    if (link) { navigate(link); return; }
    // No deep-link (respond/nurture) → the action happens here: jump to & focus the reply composer.
    composerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => composerRef.current?.focus(), 250);
  };

  const uploadInternalAttachment = async (file) => {
    if (!file) return;
    setUploadingInternalAttachment(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("conversation_id", cid);
      const { data: d } = await api.post("/communications/internal/attachment-draft", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setInternalAttachments(prev => [...prev, { id: d.id, filename: d.filename }]);
    } catch (e) { toast.error(e.response?.data?.detail || "File upload failed"); }
    finally { setUploadingInternalAttachment(false); if (internalFileRef.current) internalFileRef.current.value = ""; }
  };

  const send = async (action = "keep_open") => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      if (noteMode) {
        await api.post(`/communications/conversations/${cid}/internal-note`, { body: reply });
      } else if (conv.channel === "internal") {
        // Internal channel: use dedicated endpoint (enforces team/announcement authorization)
        await api.post(`/communications/conversations/${cid}/internal-message`, {
          body: reply,
          attachment_ids: internalAttachments.map(a => a.id),
        });
        setInternalAttachments([]);
      } else {
        const { data: res } = await api.post(`/communications/conversations/${cid}/reply`, { body: reply, channel: conv.channel === "system" ? "portal" : conv.channel, action });
        if (res?.channel_status && res.channel_status !== "live") toast.info("Message saved — this channel has no live provider, so it's queued (placeholder).");
        if (action === "clear") toast.success("Sent & cleared — conversation moved to CRM history.");
      }
      setReply(""); await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); } finally { setSending(false); }
  };
  const resolve = async () => { try { await api.post(`/communications/conversations/${cid}/status`, { status: "resolved" }); toast.success("Resolved"); load(); onChanged?.(); } catch { toast.error("Failed"); } };
  const clearQueue = async () => {
    try {
      await api.post(`/communications/conversations/${cid}/clear`);
      toast.success("Cleared from queue — conversation preserved in CRM history.");
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to clear conversation"); }
  };
  const markRead = async () => { try { await api.post(`/communications/conversations/${cid}/mark-read`); toast.success("Marked as read"); load(); onChanged?.(); } catch { toast.error("Failed"); } };
  const consultAllison = async () => {
    if (consulting) return;
    setConsulting(true);
    const t = toast.loading("Consulting Allison — analyzing this conversation…");
    try {
      const { data: res } = await api.post(`/communications/conversations/${cid}/consult-allison`);
      toast.success(res?.source === "llm" ? "Allison added her tax analysis" : "Allison responded", { id: t });
      if (res?.analysis) setAllison({ text: res.analysis, at: new Date().toISOString(), source: res.source });
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not consult Allison", { id: t }); }
    finally { setConsulting(false); }
  };

  // A2: Email classification advisory
  const classifyEmail = async () => {
    setEmailAdvisory({ loading: true });
    const t = toast.loading("Allison is classifying this email…");
    try {
      const { data: res } = await api.post(`/communications/conversations/${cid}/classify-inbound-email`);
      toast.success("Allison classification ready", { id: t });
      setEmailAdvisory({ ...res.advisory, sender_email: res.sender_email, loading: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Classification unavailable", { id: t });
      setEmailAdvisory(null);
    }
  };

  // A3: Block sender and archive conversation
  const blockAndArchive = async ({ reason, notes, confirm }) => {
    setSending(true);
    try {
      const { data: res } = await api.post(`/communications/conversations/${cid}/block-and-archive`, { reason, notes, confirm_client_associated: confirm });
      if (res.warning) {
        toast.warning(res.message);
        return res; // caller handles warning confirmation
      }
      toast.success(res.message || "Sender blocked and conversation archived");
      setBlockModal(false);
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Block failed"); }
    finally { setSending(false); }
  };

  // Trash a single message (email only)
  const trashMessage = async (msg) => {
    setSending(true);
    const t = toast.loading("Moving message to Trash…");
    try {
      await api.post(`/communications/messages/${msg.id}/trash`);
      toast.success("Message moved to Trash", { id: t });
      setTrashMsgDialog(null);
      await load(); onChanged?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const errMsg = (typeof detail === "object" ? detail?.message : detail) || "Failed to move to Trash";
      toast.error(errMsg, { id: t });
      setTrashMsgDialog(null);
    } finally { setSending(false); }
  };

  // Trash entire email conversation
  const trashConversation = async () => {
    setSending(true);
    const t = toast.loading("Moving conversation to Trash…");
    try {
      const { data: res } = await api.post(`/communications/conversations/${cid}/trash-emails`);
      if (res.partial_failure) {
        toast.warning(`Moved to Trash — ${res.titan_failed} message(s) could not be synced to Titan. Check audit log for details.`, { id: t });
      } else {
        toast.success("Conversation moved to Trash", { id: t });
      }
      setTrashConvDialog(false);
      await load(); onChanged?.();
      onClose?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === "object" ? detail?.message : detail) || "Failed to move to Trash", { id: t });
    } finally { setSending(false); }
  };

  // Restore conversation from Trash
  const restoreFromTrash = async () => {
    setSending(true);
    const t = toast.loading("Restoring conversation from Trash…");
    try {
      await api.post(`/communications/conversations/${cid}/restore-from-trash`);
      toast.success("Conversation restored", { id: t });
      await load(); onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to restore", { id: t });
    } finally { setSending(false); }
  };

  const HItem = ({ icon, label, value, tone }) => {    const Ic = Icons[icon] || Icons.Circle;
    return (
      <div className="flex items-center gap-2.5">
        <div className="h-9 w-9 rounded-lg bg-slate-50 flex items-center justify-center shrink-0"><Ic className="h-4 w-4 text-slate-500" /></div>
        <div className="min-w-0"><div className="text-[11px] text-slate-400 uppercase tracking-wide">{label}</div><div className={`text-sm font-medium truncate ${tone || "text-slate-800"}`}>{value || "—"}</div></div>
      </div>
    );
  };
  const TABS = [["conversation", "Conversation", null], ["attachments", "Attachments", data.documents?.length], ["notes", "Internal Notes", messages.filter((m) => m.internal_note).length], ["tasks", "Tasks", data.tasks?.length], ["info", "Information Requests", data.information_requests?.length], ["timeline", "Timeline", null]];

  // Case tab helper row
  const CaseRow = ({ label, value, tone, fallback = "—" }) => (
    <div className="flex items-start justify-between gap-3 py-2">
      <span className="text-[11px] uppercase tracking-wide text-slate-400 shrink-0 pt-0.5">{label}</span>
      <span className={`text-sm font-medium text-right break-words ${value ? (tone || "text-slate-800") : "text-slate-400 italic text-xs"}`}>{value ?? fallback}</span>
    </div>
  );

  // ── END: AllisonPanel replaced by inline combined panel (see Row 3 below) ──

  return (
    <div className="flex-1 flex flex-col min-h-0 w-full bg-white" data-testid="cc-workspace-shell">

      {/* ── Row 1: Contact header ──────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-200 px-5 py-3.5 shrink-0" data-testid="cc-intelligence-header">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar name={displayName} url={client?.photo_url} size="h-12 w-12" color={isInquiry ? "bg-orange-500" : "bg-blue-500"} />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-heading font-bold text-slate-900 text-lg leading-tight">{displayName}</h2>
                {isInternal
                  ? <span className="text-[10px] font-bold uppercase bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">Internal Staff</span>
                  : conv.channel === "email" && conv.correspondent_type && (conv.correspondent_type !== "spam" || conv.is_email_blocked || conv.is_spam)
                    ? ctBadge(conv.correspondent_type)
                    : isInquiry
                      ? <span className="text-[10px] font-bold uppercase bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">New Inquiry</span>
                      : <span className="text-[11px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full capitalize border border-blue-100">{client?.lifecycle_status || "client"}</span>
                }
              </div>
              <div className="text-sm text-slate-500 mt-0.5 truncate">
                {isInternal
                  ? ({"announcement": "Firm Announcement", "team_room": "Team Room"}[conv.internal_type] || "Direct Message")
                  : conv.contact_email ? conv.contact_email : isInquiry ? `Inquiry via ${ch(intake.inquiry_source).label}` : (data.engagement?.engagement_number || "No engagement")}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isInquiry && (
              (conv.assigned_to && me?.id && conv.assigned_to === me.id)
                ? <span data-testid="cc-claimed-by-you" className="inline-flex items-center gap-1.5 text-sm bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg px-3 py-1.5 font-medium"><Icons.CheckCircle2 className="h-4 w-4" />Yours</span>
                : <button disabled={sending} onClick={claimLead} data-testid="cc-claim-lead" className="inline-flex items-center gap-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-3.5 py-1.5 font-medium disabled:opacity-50">
                    {sending ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Hand className="h-4 w-4" />}Claim
                  </button>
            )}
            {isInquiry
              ? <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button disabled={sending} data-testid="cc-assign-team" className="inline-flex items-center gap-2 text-sm bg-orange-600 hover:bg-orange-700 text-white rounded-lg px-3.5 py-1.5 font-medium disabled:opacity-50">
                      {sending ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Users className="h-4 w-4" />}Assign<Icons.ChevronDown className="h-3.5 w-3.5 opacity-80" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>Assign to Team</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {teams.length === 0 && <DropdownMenuItem disabled>No teams available</DropdownMenuItem>}
                    {teams.map((t) => (
                      <DropdownMenuItem key={t.key} data-testid={`cc-assign-team-${t.key}`} onClick={() => assignToTeam(t)}
                        className={intake?.assigned_team_id === t.key ? "text-orange-700 font-medium" : ""}>
                        <Icons.Users className="h-3.5 w-3.5 mr-2 text-slate-400" />{t.name}
                        {intake?.assigned_team_id === t.key && <Icons.Check className="h-3.5 w-3.5 ml-auto text-orange-600" />}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              : client?.id && <button onClick={() => navigate(`/client-workspace/${client.id}`)} data-testid="cc-open-workspace" className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-600 hover:text-white border border-blue-200 hover:border-blue-600 rounded-lg px-3 py-1.5 transition-colors">Open Workspace <Icons.ArrowRight className="h-3.5 w-3.5" /></button>}
            {conv.channel === "email" && !conv.is_historical_import && (
              <button onClick={() => setBlockModal(true)} data-testid="cc-block-sender-btn"
                className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-lg px-3 py-1.5 transition-colors border ${conv.is_email_blocked ? "bg-rose-50 text-rose-700 border-rose-200 cursor-default" : "text-slate-500 border-slate-200 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200"}`}
                disabled={!!conv.is_email_blocked}>
                <Icons.Ban className="h-3.5 w-3.5" />{conv.is_email_blocked ? "Blocked" : "Spam & Block"}
              </button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="text-slate-400 hover:text-slate-700 p-2"><Icons.MoreHorizontal className="h-4 w-4" /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={markRead}><Icons.MailOpen className="h-4 w-4 mr-2" />Mark Read</DropdownMenuItem>
                <DropdownMenuItem onClick={resolve}><Icons.CheckCircle2 className="h-4 w-4 mr-2 text-emerald-600" />Resolve</DropdownMenuItem>
                {isMgmt && !isInquiry && <DropdownMenuItem onClick={() => setDialog({ type: "assign" })}><Icons.UserPlus className="h-4 w-4 mr-2" />Assign Staff</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setDialog({ type: "escalate" })}><Icons.AlertTriangle className="h-4 w-4 mr-2 text-rose-500" />Escalate</DropdownMenuItem>
                {conv.channel === "email" && !conv.is_historical_import && !conv.is_trashed && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid="cc-delete-conv-btn" onClick={() => setTrashConvDialog(true)} className="text-rose-600 focus:text-rose-600 focus:bg-rose-50">
                      <Icons.Trash2 className="h-4 w-4 mr-2" />Delete Conversation
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            {!railOpen && (
              <button onClick={() => setRailOpen(true)} data-testid="cc-rail-reopen" className="text-slate-400 hover:text-indigo-600 p-2" title="Show Allison · Case panel">
                <Icons.PanelRight className="h-4 w-4" />
              </button>
            )}
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-2"><Icons.X className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      {/* ── Row 2: Tabs bar ────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-200 px-5 shrink-0">
        <div className="flex items-center gap-0 overflow-x-auto" data-testid="cc-section-tabs">
          {TABS.map(([k, l, n]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`cc-tab-${k}`}
              className={`text-sm px-4 py-3 border-b-2 whitespace-nowrap transition-colors ${tab === k ? "border-blue-600 text-blue-700 font-semibold" : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"}`}>
              {l}{n ? <span className="ml-1.5 text-[10px] bg-slate-100 text-slate-600 rounded-full px-1.5 py-0.5">{n}</span> : null}
            </button>
          ))}
        </div>
      </div>

      {/* ── Row 3: Middle area (thread + Allison panel) ────────────────────── */}
      {/* Trash banner: shown when this conversation is in Trash */}
      {conv.is_trashed && (
        <div className="px-5 py-3 bg-rose-50 border-b border-rose-200 flex items-center gap-3 shrink-0" data-testid="cc-trash-banner">
          <Icons.Trash2 className="h-4 w-4 text-rose-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-rose-800">This conversation is in Trash</div>
            <div className="text-xs text-rose-600 mt-0.5">Emails have been moved to the Titan Trash folder. Restore to make them active again.</div>
          </div>
          <button onClick={restoreFromTrash} disabled={sending} data-testid="cc-restore-from-trash"
            className="inline-flex items-center gap-1.5 text-xs font-medium bg-rose-600 text-white hover:bg-rose-700 rounded-lg px-3 py-1.5 disabled:opacity-50 transition-colors shrink-0">
            {sending ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icons.RotateCcw className="h-3.5 w-3.5" />}Restore
          </button>
        </div>
      )}
      <div className="flex-1 min-h-0 flex overflow-hidden">
        {/* Thread / tab content */}
        <div className="flex-1 min-w-0 overflow-y-auto tf-scroll px-5 py-4 bg-slate-50/40" data-testid="cc-thread">
          {tab === "conversation" && (
            <Thread
              messages={isInternal ? messages : messages.filter((m) => !m.internal_note)}
              bottomRef={bottomRef}
              channel={conv.channel}
              onReply={conv.channel === "email" && !conv.is_historical_import && !conv.is_email_blocked && !conv.is_trashed
                ? (m) => setComposer({ mode: "reply", parentMessage: m })
                : null}
              onReplyAll={conv.channel === "email" && !conv.is_historical_import && !conv.is_email_blocked && !conv.is_trashed
                ? (m) => setComposer({ mode: "reply_all", parentMessage: m })
                : null}
              onForward={conv.channel === "email" && !conv.is_historical_import && !conv.is_trashed
                ? (m) => setComposer({ mode: "forward", parentMessage: m })
                : null}
              onTrash={conv.channel === "email" && !conv.is_historical_import && !conv.is_trashed
                ? (m) => setTrashMsgDialog(m)
                : null}
            />
          )}
          {tab === "attachments" && (
            <AttachmentsTab documents={data.documents} cid={cid} />
          )}
          {tab === "notes" && <Thread messages={messages.filter((m) => m.internal_note)} bottomRef={bottomRef} notes channel={conv.channel} />}
          {tab === "tasks" && <SimpleList items={data.tasks} render={(t) => <span>{t.title} <span className={`text-[10px] px-1.5 rounded ${PRIORITY_TONE[t.priority]}`}>{t.priority}</span></span>} empty="No open tasks." testid="cc-tasks" />}
          {tab === "info" && <SimpleList items={data.information_requests} render={(i) => <span>{i.title} <span className="text-slate-400">· {i.status}</span></span>} empty="No information requests." testid="cc-info" />}
          {tab === "timeline" && <Timeline clientId={client?.id} />}
        </div>

        {/* Allison / Case Context — far-right panel, all conversation types, collapsible */}
        {railOpen && (
          <aside className="w-80 shrink-0 border-l border-slate-200 bg-white flex flex-col min-h-0" data-testid="cc-allison-case-panel">
            {/* Panel header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 shrink-0">
              <div className="flex items-center gap-2">
                <div className="h-6 w-6 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
                  <Icons.Sparkles className="h-3 w-3 text-indigo-600" />
                </div>
                <span className="font-semibold text-slate-800 text-sm">Allison · Case</span>
              </div>
              <button onClick={() => setRailOpen(false)} data-testid="cc-rail-collapse" className="text-slate-400 hover:text-slate-600 p-1" title="Collapse panel">
                <Icons.PanelRightClose className="h-4 w-4" />
              </button>
            </div>
            {/* Tabs */}
            <div className="flex border-b border-slate-100 shrink-0">
              <button onClick={() => setAllisonTab("allison")} data-testid="cc-allison-tab-btn"
                className={`flex-1 py-2.5 text-xs font-semibold transition-colors border-b-2 ${allisonTab === "allison" ? "border-indigo-500 text-indigo-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
                Allison
              </button>
              <button onClick={() => setAllisonTab("case")} data-testid="cc-case-tab-btn"
                className={`flex-1 py-2.5 text-xs font-semibold transition-colors border-b-2 ${allisonTab === "case" ? "border-blue-500 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
                Case
              </button>
            </div>
            {/* Tab content */}
            <div className="flex-1 overflow-y-auto tf-scroll min-h-0">
              {allisonTab === "allison" ? (
                <div className="p-3 space-y-3">
                  {/* ── ALLISON QUICK REVIEW ─────────────────────────── */}
                  {quickReviewLoading ? (
                    <div className="flex items-center gap-2 py-4 text-xs text-slate-400">
                      <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading quick review…
                    </div>
                  ) : quickReview ? (
                    <div className="space-y-3" data-testid="allison-quick-review">
                      {/* A — Message Summary */}
                      {quickReview.message_summary && (
                        <div>
                          <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Message Summary</div>
                          <p className="text-xs text-slate-700 leading-relaxed">{quickReview.message_summary}</p>
                        </div>
                      )}

                      {/* B — Relationship Status */}
                      <div>
                        <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Relationship Status</div>
                        {(() => {
                          // Handle both internal (nested .relationship) and external (flat fields) response shapes
                          const lbl = quickReview.relationship_label || quickReview.relationship?.label;
                          const rs  = quickReview.relationship_status || quickReview.relationship?.key;
                          const tone = rs === "client" ? "bg-blue-100 text-blue-700 border-blue-200"
                            : rs === "existing_lead" ? "bg-orange-100 text-orange-700 border-orange-200"
                            : rs === "new_inquiry" || rs === "prospect" ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                            : rs === "staff" || rs === "internal" ? "bg-slate-100 text-slate-600 border-slate-200"
                            : rs === "spam" || rs === "blocked" ? "bg-rose-100 text-rose-700 border-rose-200"
                            : "bg-amber-100 text-amber-700 border-amber-200";
                          return (
                            <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-lg border ${tone}`} data-testid="aqr-relationship-badge">
                              {lbl || rs}
                            </span>
                          );
                        })()}
                      </div>

                      {/* C — Key Context */}
                      {quickReview.key_context?.length > 0 && (
                        <div>
                          <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Key Context</div>
                          <ul className="space-y-1" data-testid="aqr-key-context">
                            {quickReview.key_context.map((fact, i) => (
                              <li key={i} className="flex items-start gap-1.5 text-xs text-slate-600">
                                <Icons.Dot className="h-3 w-3 text-slate-300 shrink-0 mt-0.5" />{fact}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* D — Recommended Next Action */}
                      <div>
                        <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Recommended Action</div>

                        {/* E — Convert to Lead & Invite */}
                        {convertResult ? (
                          /* Post-conversion status (inline compact) */
                          <div className="space-y-2 p-3 bg-emerald-50 rounded-xl border border-emerald-200" data-testid="post-conversion-status">
                            <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-800">
                              <Icons.CheckCircle2 className="h-3.5 w-3.5" />Lead Created · #{convertResult.client_number}
                            </div>
                            <div className="space-y-1 text-xs">
                              {[
                                ["Portal Invitation", convertResult.portal_invite_status],
                                ["Welcome Email", convertResult.welcome_email_status],
                              ].map(([lbl, st]) => (
                                <div key={lbl} className="flex items-center justify-between">
                                  <span className="text-slate-500">{lbl}</span>
                                  <span className={`font-medium ${st === "invitation_sent" || st === "sent" || st === "previously_sent" ? "text-emerald-700" : st === "failed" ? "text-rose-600" : "text-amber-600"}`}>
                                    {st === "invitation_sent" ? "Sent" : st === "sent" ? "Sent" : st === "previously_sent" ? "Already Sent" : st === "failed" ? "Failed" : st || "Pending"}
                                  </span>
                                </div>
                              ))}
                            </div>
                            {convertResult.lead_id && (
                              <a href={`/clients/${convertResult.lead_id}`} target="_blank" rel="noreferrer"
                                className="text-[10px] font-semibold text-blue-600 hover:underline flex items-center gap-1" data-testid="aqr-open-lead-link">
                                <Icons.ExternalLink className="h-3 w-3" />Open Lead Workspace
                              </a>
                            )}
                          </div>
                        ) : quickReview.can_convert ? (
                          <button onClick={() => setShowConvertDialog(true)} data-testid="aqr-convert-btn"
                            className="w-full flex items-center justify-center gap-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg px-3 py-2.5 transition-colors">
                            <Icons.UserPlus className="h-3.5 w-3.5" />Convert to Lead & Invite
                          </button>
                        ) : (
                          <div className="space-y-1.5">
                            <div className="text-xs text-slate-600 bg-slate-50 px-3 py-2 rounded-lg border border-slate-200" data-testid="aqr-recommended-action">
                              {quickReview.recommended_action}
                            </div>
                            {quickReview.conversion_blocked_reason && (
                              <p className="text-[10px] text-amber-600">{quickReview.conversion_blocked_reason}</p>
                            )}
                          </div>
                        )}
                        <p className="text-[10px] text-slate-400 mt-1.5">Allison advisory only — all actions require staff confirmation.</p>
                      </div>

                      {/* Existing linked record shortcut */}
                      {(quickReview.linked_record || quickReview.existing_lead || quickReview.existing_client) && !convertResult && (
                        <a href={`/clients/${(quickReview.linked_record || quickReview.existing_lead || quickReview.existing_client)?.id}`}
                          target="_blank" rel="noreferrer"
                          className="text-[10px] font-semibold text-blue-600 hover:underline flex items-center gap-1">
                          <Icons.ExternalLink className="h-3 w-3" />
                          Open {(quickReview.linked_record || quickReview.existing_lead)?.full_name || "CRM Record"}
                        </a>
                      )}

                      {/* Deep Analysis (preserved but secondary) */}
                      {allison && (
                        <details className="text-xs">
                          <summary className="cursor-pointer text-[10px] font-semibold text-slate-400 uppercase tracking-wider hover:text-slate-600 select-none">
                            Full Allison Analysis
                          </summary>
                          <div className="mt-2 text-xs text-slate-700 leading-relaxed bg-indigo-50/60 rounded-lg p-3 border border-indigo-100 allison-md"
                            dangerouslySetInnerHTML={{ __html: mdToHtml(allison.text) }} />
                          <div className="text-[10px] text-slate-400 mt-1">{new Date(allison.at).toLocaleString()}</div>
                        </details>
                      )}

                      {/* Secondary: Consult Allison for deep analysis */}
                      <button onClick={consultAllison} disabled={consulting} data-testid="cc-action-consult-allison"
                        className="w-full text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg px-3 py-2 flex items-center justify-center gap-1.5 disabled:opacity-60 transition-colors">
                        {consulting ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icons.Sparkles className="h-3.5 w-3.5" />}
                        {allison ? "Re-run Deep Analysis" : "Consult Allison"}
                      </button>
                    </div>
                  ) : (
                    /* Fallback: no quick review data */
                    <div className="space-y-3">
                      <p className="text-xs text-slate-500 leading-relaxed">Allison can analyze this conversation, suggest a next action, and recommend which team to assign to.</p>
                      <button onClick={consultAllison} disabled={consulting} data-testid="cc-action-consult-allison"
                        className="w-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 rounded-lg px-3 py-2.5 flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
                        {consulting ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Sparkles className="h-4 w-4" />}Consult Allison
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {/* ── Contact Identity ─────────────────────────────── */}
                  <div className="px-4 py-3 bg-slate-50/50" data-testid="cc-case-identity">
                    <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2.5">Correspondent</div>
                    <div className="flex items-start gap-2.5 mb-2">
                      <div className="h-8 w-8 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0">
                        <Icons.User className="h-4 w-4 text-slate-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-slate-800 truncate">{displayName}</div>
                        {conv.contact_email && (
                          <div className="text-xs text-slate-500 truncate">{conv.contact_email}</div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {(conv.correspondent_type || quickReview?.relationship_status) &&
                        ctBadge(conv.correspondent_type || quickReview?.relationship_status, true)}
                      <span className="inline-flex items-center gap-1 text-[10px] text-slate-500 bg-white border border-slate-200 px-2 py-0.5 rounded-full">
                        {(() => { const Ic = Icons[ch(conv.channel).icon] || Icons.Mail; return <Ic className="h-2.5 w-2.5" />; })()}
                        {ch(conv.channel).label}
                      </span>
                    </div>
                    {client?.id && (
                      <a href={`/clients/${client.id}`} target="_blank" rel="noreferrer"
                        className="mt-2 text-[10px] font-semibold text-blue-600 hover:underline flex items-center gap-1" data-testid="cc-case-open-crm">
                        <Icons.ExternalLink className="h-3 w-3" />Open CRM Record
                      </a>
                    )}
                  </div>
                  {/* ── Case fields (factual CRM and conversation data only) ── */}
                  <div className="px-4 py-2 divide-y divide-slate-100">
                    <CaseRow label="Conv. Status" value={(conv.conv_status || "open").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())} tone="text-slate-700" />
                    {isInquiry ? (
                      <>
                        <CaseRow label="Inquiry Status" value={(intake?.inquiry_status || "").replace(/_/g, " ") || null} tone="text-orange-600" fallback="Not Yet Set" />
                        <CaseRow label="Owner" value={intake?.assigned_user_name} fallback="Unassigned" />
                        <CaseRow label="Team" value={teams.find(t => t.key === intake?.assigned_team_id)?.name} fallback="Unassigned" />
                        <CaseRow label="Priority" value={intake?.priority} tone={intake?.priority === "high" ? "text-rose-600" : "text-amber-600"} fallback="Not Set" />
                        <CaseRow label="Source" value={ch(intake?.inquiry_source).label} />
                      </>
                    ) : (
                      <>
                        <CaseRow label="Department" value={ctx?.department} fallback="Unassigned" />
                        <CaseRow label="Stage" value={ctx?.workflow_stage} tone="text-blue-600" fallback="Not Yet Set" />
                        <CaseRow label="Owner" value={ctx?.assigned_user_name} fallback="Unassigned" />
                        <CaseRow label="Priority" value={ctx?.priority} tone={ctx?.priority === "high" ? "text-rose-600" : "text-amber-600"} fallback="Not Set" />
                        {ctx?.payment?.status && ctx.payment.status !== "none" && (
                          <CaseRow label="Payment" value={ctx?.payment?.label} />
                        )}
                        {ctx?.organizer?.status && (
                          <CaseRow label="Organizer" value={ctx?.organizer?.label} />
                        )}
                        {(ctx?.documents?.total ?? 0) > 0 && (
                          <CaseRow label="Doc. Checklist" value={`${ctx?.documents?.received ?? 0} of ${ctx?.documents?.total} received`} />
                        )}
                        {(ctx?.open_information_requests ?? 0) > 0 && (
                          <CaseRow label="Open IRs" value={ctx?.open_information_requests} tone="text-rose-600" />
                        )}
                      </>
                    )}
                  </div>
                  {/* Historical Communications */}
                  {client?.id && !histError && histCount != null && histCount > 0 && !isHistorical && (
                    <div className="px-4 py-2.5" data-testid="cc-rail-historical-section">
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-[11px] uppercase tracking-wide text-slate-400 shrink-0 pt-0.5">Historical</span>
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium text-slate-800" data-testid="cc-rail-historical-count">{histCount} email{histCount !== 1 ? "s" : ""}</span>
                          <button onClick={() => navigate(`/communications/inbox?historical_for=${client.id}`)} data-testid="cc-rail-historical-view"
                            className="text-xs text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-0.5 shrink-0 font-medium">
                            View <Icons.ArrowUpRight className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </aside>
        )}
      </div>

      {/* ── Row 4: Action bar + compose area ──────────────────────────────── */}
      <div className="bg-white border-t border-slate-200 shrink-0 flex flex-col" data-testid="cc-bottom-bar">
        {isHistorical ? (
          <div className="px-4 py-3 flex items-center gap-3 bg-amber-50 border-b border-amber-100" data-testid="cc-historical-readonly-banner">
            <Icons.Archive className="h-4 w-4 text-amber-600 shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-medium text-amber-800">Historical record · Read only</div>
              <div className="text-xs text-amber-700 mt-0.5">Imported communication. Reply and write operations are disabled.</div>
            </div>
          </div>
        ) : conv.is_trashed ? (
          <div className="px-4 py-3 flex items-center gap-2 bg-rose-50/60" data-testid="cc-trash-compose-disabled">
            <Icons.Trash2 className="h-4 w-4 text-rose-400 shrink-0" />
            <span className="text-sm text-rose-600">Replies are disabled for trashed conversations.</span>
          </div>
        ) : (
          <>
            {/* Action bar - always visible */}
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100 shrink-0" data-testid="cc-action-bar">
              {conv.channel !== "email" && (
                <button onClick={() => { setNoteMode(false); composerRef.current?.focus(); }} data-testid="cc-action-reply"
                  className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg font-semibold bg-slate-900 text-white hover:bg-slate-700 transition-colors">
                  <Icons.Reply className="h-4 w-4" />Reply
                </button>
              )}
              {isMgmt && !isInquiry && (
                <button onClick={() => setDialog({ type: "assign" })} data-testid="cc-action-assign"
                  className="inline-flex items-center gap-1.5 text-sm px-3.5 py-2 rounded-lg font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors">
                  <Icons.UserPlus className="h-4 w-4" />Assign
                </button>
              )}
              <button onClick={resolve} data-testid="cc-action-resolve"
                className="inline-flex items-center gap-1.5 text-sm px-3.5 py-2 rounded-lg font-medium border border-emerald-300 text-emerald-700 hover:bg-emerald-50 transition-colors">
                <Icons.CheckCircle2 className="h-4 w-4" />Resolve
              </button>
              <div className="flex-1" />
              {!railOpen && (
                <button onClick={() => setRailOpen(true)} data-testid="cc-rail-reopen-bar"
                  className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg font-medium border border-indigo-200 text-indigo-600 hover:bg-indigo-50 transition-colors">
                  <Icons.PanelRight className="h-3.5 w-3.5" />AI · Case
                </button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="cc-action-more" className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg font-medium border border-slate-300 text-slate-700 hover:bg-slate-50">
                    <Icons.MoreHorizontal className="h-4 w-4" />More
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem data-testid="cc-more-note" onClick={() => { setNoteMode(true); composerRef.current?.focus(); }}><Icons.Lock className="h-4 w-4 mr-2" />Internal Note</DropdownMenuItem>
                  {conv.channel !== "email" && <DropdownMenuItem data-testid="cc-more-template" onClick={() => setShowTpl(true)}><Icons.Zap className="h-4 w-4 mr-2" />Quick Reply</DropdownMenuItem>}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="cc-more-task" onClick={() => setDialog({ type: "task" })}><Icons.CheckSquare className="h-4 w-4 mr-2" />Create Task</DropdownMenuItem>
                  <DropdownMenuItem data-testid="cc-more-docs" onClick={() => setDialog({ type: "ir", doc: true })}><Icons.FileUp className="h-4 w-4 mr-2" />Request Documents</DropdownMenuItem>
                  <DropdownMenuItem data-testid="cc-more-ir" onClick={() => setDialog({ type: "ir" })}><Icons.HelpCircle className="h-4 w-4 mr-2" />Info Request</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="cc-more-escalate" onClick={() => setDialog({ type: "escalate" })}><Icons.AlertTriangle className="h-4 w-4 mr-2" />Escalate</DropdownMenuItem>
                  <DropdownMenuItem data-testid="cc-more-mark-read" onClick={markRead}><Icons.MailOpen className="h-4 w-4 mr-2" />Mark Read</DropdownMenuItem>
                  {conv.channel !== "internal" && conv.queue_status !== "cleared" && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem data-testid="cc-more-clear-queue" onClick={clearQueue}
                        className="text-emerald-700 focus:text-emerald-700 focus:bg-emerald-50">
                        <Icons.CheckCircle2 className="h-4 w-4 mr-2" />Clear from Queue
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Compose area: expandable — email uses TipTap ComposerModal, others use textarea */}
            <div className={`overflow-y-auto tf-scroll transition-all duration-200 ${composerExpanded ? "flex-1 min-h-[420px]" : "max-h-[400px]"}`}>
            {conv.channel === "email" && !conv.is_email_blocked ? (
              composer ? (
                <ComposerModal
                  key={`${composer.mode}-${composer.parentMessage?.id}`}
                  mode={composer.mode}
                  conversation={conv}
                  parentMessage={composer.parentMessage}
                  isInline
                  composerExpanded={composerExpanded}
                  onToggleExpand={() => setComposerExpanded(v => !v)}
                  onDiscard={() => setComposer(null)}
                  onSent={(action) => {
                    setComposer(null);
                    if (action === "clear") toast.success("Sent & cleared — conversation moved to CRM history.");
                    load(); onChanged?.();
                  }}
                />
              ) : null
            ) : (
              <div className="px-4 pb-4 pt-2">
                {/* Announcement read-only gate for non-management staff */}
                {conv.channel === "internal" && conv.internal_type === "announcement" && !isMgmt ? (
                  <div className="border border-violet-200 bg-violet-50 rounded-xl p-4 text-center">
                    <Icons.Megaphone className="h-5 w-5 mx-auto mb-2 text-violet-500" />
                    <div className="text-sm font-medium text-violet-700">Firm Announcement — Read Only</div>
                    <div className="text-xs text-violet-500 mt-1">Only management can post to announcements. Use <strong>New Message</strong> to respond privately.</div>
                  </div>
                ) : (
                  <>
                    {/* Internal attachment preview bar */}
                    {conv.channel === "internal" && !noteMode && internalAttachments.length > 0 && (
                      <div className="mb-2 flex flex-wrap gap-1.5">
                        {internalAttachments.map(a => (
                          <div key={a.id} className="flex items-center gap-1 bg-violet-50 border border-violet-200 rounded-lg px-2 py-1 text-xs text-violet-700">
                            <Icons.Paperclip className="h-3 w-3 shrink-0" />
                            <span className="max-w-[120px] truncate">{a.filename}</span>
                            <button onClick={() => setInternalAttachments(prev => prev.filter(x => x.id !== a.id))} className="text-violet-400 hover:text-violet-700 ml-0.5"><Icons.X className="h-3 w-3" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                    {showTpl && (
                      <div className="mb-2 max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-sm z-10 p-1" data-testid="cc-template-menu">
                        <div className="flex items-center justify-between px-2 py-1"><span className="text-xs font-medium text-slate-500">Templates</span><button onClick={() => setShowTpl(false)} className="text-slate-400 hover:text-slate-700"><Icons.X className="h-3.5 w-3.5" /></button></div>
                        {tpls.length === 0 ? <div className="p-3 text-xs text-slate-400">No templates.</div> : tpls.map((t) => (
                          <button key={t.id} onClick={() => { setReply(t.body); setShowTpl(false); setNoteMode(false); composerRef.current?.focus(); }} className="w-full text-left px-2.5 py-2 rounded hover:bg-slate-50 text-sm"><div className="font-medium text-slate-700">{t.name}</div></button>
                        ))}
                      </div>
                    )}
                    <div className={`border rounded-xl ${noteMode ? "border-amber-300 bg-amber-50/40" : conv.channel === "internal" ? "border-violet-200" : "border-slate-200"}`}>
                      <textarea value={reply} onChange={(e) => setReply(e.target.value)} data-testid="cc-composer" ref={composerRef}
                        placeholder={noteMode ? "Add an internal note (staff-only)…" : conv.channel === "internal" ? "Write an internal message…" : "Type your message…"} rows={2}
                        className="w-full bg-transparent px-3 py-2.5 text-sm focus:outline-none resize-none" />
                      <div className="flex items-center justify-between px-3 pb-2">
                        {conv.channel === "internal" && !noteMode ? (
                          <label htmlFor={`int-attach-${cid}`} className="cursor-pointer inline-flex items-center gap-1 text-slate-400 hover:text-violet-600 transition-colors px-1 py-0.5 rounded" title="Attach file">
                            {uploadingInternalAttachment ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin text-violet-500" /> : <Icons.Paperclip className="h-3.5 w-3.5" />}
                            <input id={`int-attach-${cid}`} ref={internalFileRef} type="file" className="hidden" onChange={(e) => uploadInternalAttachment(e.target.files?.[0])} />
                          </label>
                        ) : (
                          <span className="text-xs text-slate-400">{noteMode ? "Internal note" : `Reply via ${ch(conv.channel==="system"?"portal":conv.channel).label}`}</span>
                        )}
                        {noteMode || conv.channel === "internal" ? (
                          <button onClick={() => send()} disabled={sending||!reply.trim()} data-testid="cc-send"
                            className={`inline-flex items-center gap-2 text-white rounded-lg px-4 py-1.5 text-sm font-medium disabled:opacity-50 ${noteMode ? "bg-amber-500 hover:bg-amber-600" : "bg-violet-600 hover:bg-violet-700"}`}>
                            {sending ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Send className="h-4 w-4" />}
                            {noteMode ? "Save Note" : "Send"}
                          </button>
                        ) : (
                          <div className="relative inline-flex" ref={sendMenuRef}>
                            <div className="inline-flex rounded-lg overflow-hidden border border-slate-900">
                              <button onClick={() => { setShowSendMenu(false); send("keep_open"); }} disabled={sending||!reply.trim()} data-testid="cc-send"
                                className="inline-flex items-center gap-2 text-white bg-slate-900 hover:bg-slate-700 pl-4 pr-3 py-1.5 text-sm font-medium disabled:opacity-50">
                                {sending ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Send className="h-4 w-4" />}Send &amp; Keep Open
                              </button>
                              <div className="w-px bg-white/20 self-stretch" />
                              <button onClick={() => setShowSendMenu(p => !p)} disabled={sending||!reply.trim()} data-testid="cc-send-menu-toggle"
                                className="inline-flex items-center justify-center px-2 py-1.5 text-white bg-slate-900 hover:bg-slate-700 disabled:opacity-50">
                                <Icons.ChevronDown className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            {showSendMenu && (
                              <div className="absolute bottom-full right-0 mb-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-50 overflow-hidden" data-testid="cc-send-menu">
                                <button onClick={() => { setShowSendMenu(false); send("keep_open"); }} data-testid="cc-send-keep-open"
                                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 text-left">
                                  <Icons.Send className="h-4 w-4 text-slate-500 shrink-0" />
                                  <div><div className="font-medium">Send &amp; Keep Open</div><div className="text-[10px] text-slate-400 mt-0.5">Default — stays in queue</div></div>
                                </button>
                                <div className="border-t border-slate-100" />
                                <button onClick={() => { setShowSendMenu(false); send("clear"); }} data-testid="cc-send-clear"
                                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-slate-700 hover:bg-green-50 text-left">
                                  <Icons.CheckCheck className="h-4 w-4 text-green-600 shrink-0" />
                                  <div><div className="font-medium">Send &amp; Clear</div><div className="text-[10px] text-slate-400 mt-0.5">Done — move to CRM history</div></div>
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            </div>
          </>
        )}
      </div>

      {dialog && <ActionDialog dialog={dialog} cid={cid} clientId={client?.id} onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); onChanged?.(); }} />}

      {/* Block Sender Modal */}
      {blockModal && <BlockSenderModal conv={conv} sending={sending} onConfirm={blockAndArchive} onClose={() => setBlockModal(false)} />}

      {/* Trash Conversation Confirmation */}
      {trashConvDialog && (
        <Dialog open onOpenChange={() => setTrashConvDialog(false)}>
          <DialogContent data-testid="cc-trash-conv-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-rose-700">
                <Icons.Trash2 className="h-5 w-5" />Delete Conversation
              </DialogTitle>
            </DialogHeader>
            <div className="text-sm text-slate-700 space-y-2">
              <p>This will move all emails in this conversation to the Titan Trash folder and hide them from active views.</p>
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 space-y-1">
                <div className="font-semibold">What happens:</div>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>Identified inbound emails are moved to Titan Trash.</li>
                  <li>No CRM records, leads, invoices, or documents are affected.</li>
                  <li>You can restore this conversation from the Trash view at any time.</li>
                </ul>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <button onClick={() => setTrashConvDialog(false)} className="text-sm px-4 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50">Cancel</button>
              <button onClick={trashConversation} disabled={sending} data-testid="cc-trash-conv-confirm"
                className="text-sm px-4 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50 inline-flex items-center gap-2">
                {sending ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Trash2 className="h-4 w-4" />}Move to Trash
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Trash Single Message Confirmation */}
      {trashMsgDialog && (
        <Dialog open onOpenChange={() => setTrashMsgDialog(null)}>
          <DialogContent data-testid="cc-trash-msg-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-rose-700">
                <Icons.Trash2 className="h-5 w-5" />Delete Message
              </DialogTitle>
            </DialogHeader>
            <div className="text-sm text-slate-700 space-y-2">
              <p>Move this email to the Titan Trash folder?</p>
              {trashMsgDialog.subject && (
                <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-700">
                  <span className="font-medium">Subject: </span>{trashMsgDialog.subject}
                </div>
              )}
              <p className="text-xs text-slate-500">This action can be undone from the Trash view.</p>
            </div>
            <DialogFooter className="gap-2">
              <button onClick={() => setTrashMsgDialog(null)} className="text-sm px-4 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50">Cancel</button>
              <button onClick={() => trashMessage(trashMsgDialog)} disabled={sending} data-testid="cc-trash-msg-confirm"
                className="text-sm px-4 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50 inline-flex items-center gap-2">
                {sending ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Trash2 className="h-4 w-4" />}Move to Trash
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Convert to Lead & Invite Dialog */}
      {showConvertDialog && (
        <ConvertToLeadDialog
          conversationId={cid}
          initialQuickReview={quickReview}
          onClose={() => setShowConvertDialog(false)}
          onConverted={(result) => {
            setConvertResult(result);
            setShowConvertDialog(false);
            load(); onChanged?.();
            toast.success(`Lead created · ${result.client_number}`);
          }}
        />
      )}

    </div>
  );
}

function ActionBtn({ icon, label, onClick, active, testid }) {
  const Ic = Icons[icon] || Icons.Circle;
  return <button onClick={onClick} data-testid={testid} className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${active ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}><Ic className="h-3.5 w-3.5" />{label}</button>;
}

// ── Email message card — professional email reading style ─────────────────────
function EmailMessageCard({ m, onReply, onReplyAll, onForward, onTrash }) {
  const [expanded, setExpanded] = useState(true);
  const out = m.direction === "outbound";
  const canTrash = onTrash && !m.is_trashed && !!m.rfc_message_id && !out;

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString([], { weekday: "short", year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
  };

  return (
    <div className={`bg-white rounded-xl border shadow-sm overflow-hidden ${out ? "border-blue-100" : "border-slate-200"}`} data-testid={`cc-msg-${m.id}`}>
      {/* Email header bar */}
      <div className={`px-4 py-3 flex items-start gap-3 cursor-pointer select-none ${out ? "bg-blue-50/60" : "bg-white"}`} onClick={() => setExpanded((v) => !v)}>
        <Avatar name={m.sender_name} size="h-9 w-9" color={out ? "bg-blue-600" : "bg-slate-500"} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-semibold text-slate-900 truncate text-sm">{m.sender_name || (out ? "You" : "Unknown")}</span>
              {out && <span className="text-[10px] font-bold uppercase bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded shrink-0">Sent</span>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-slate-400">{fmtDate(m.sent_at)}</span>
              <Icons.ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
            </div>
          </div>
          {m.from_email && <div className="text-xs text-slate-500 mt-0.5 truncate">{out ? `To: ${m.to_emails?.join(", ") || m.to_email || "—"}` : `From: ${m.from_email}`}</div>}
          {!expanded && m.subject && <div className="text-xs text-slate-600 truncate mt-0.5">{m.subject}</div>}
        </div>
      </div>

      {expanded && (
        <>
          {/* Email metadata strip */}
          <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/40 text-xs text-slate-500 space-y-0.5">
            {m.subject && <div><span className="font-medium text-slate-600">Subject: </span>{m.subject}</div>}
            {m.from_email && !out && <div><span className="font-medium text-slate-600">From: </span>{m.from_email}</div>}
            {(m.to_emails || m.to_email) && <div><span className="font-medium text-slate-600">To: </span>{(m.to_emails || [m.to_email]).join(", ")}</div>}
            {m.cc_emails && m.cc_emails.length > 0 && <div><span className="font-medium text-slate-600">Cc: </span>{m.cc_emails.join(", ")}</div>}
          </div>

          {/* Email body */}
          <div className="px-4 py-4">
            {m.html_body ? (
              <div className="prose prose-sm max-w-none text-slate-800 text-sm" dangerouslySetInnerHTML={{ __html: m.html_body }} />
            ) : (
              <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{m.body || "(No body)"}</div>
            )}
          </div>

          {/* Email action bar */}
          {!out && (onReply || onReplyAll || onForward || canTrash) && (
            <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50/40 flex items-center gap-2" data-testid={`cc-email-msg-actions-${m.id}`}>
              {onReply && (
                <button onClick={() => onReply(m)} data-testid={`cc-msg-reply-${m.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 rounded-lg px-3 py-1.5 transition-colors">
                  <Icons.Reply className="h-3.5 w-3.5" />Reply
                </button>
              )}
              {onReplyAll && (
                <button onClick={() => onReplyAll(m)} data-testid={`cc-msg-reply-all-${m.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg px-3 py-1.5 transition-colors">
                  <Icons.ReplyAll className="h-3.5 w-3.5" />Reply All
                </button>
              )}
              {onForward && (
                <button onClick={() => onForward(m)} data-testid={`cc-msg-forward-${m.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-lg px-3 py-1.5 transition-colors">
                  <Icons.Forward className="h-3.5 w-3.5" />Forward
                </button>
              )}
              {canTrash && (
                <button onClick={() => onTrash(m)} data-testid={`cc-msg-trash-${m.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-lg px-3 py-1.5 transition-colors ml-auto">
                  <Icons.Trash2 className="h-3.5 w-3.5" />Delete
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Thread({ messages, bottomRef, notes, channel, onReply, onReplyAll, onForward, onTrash }) {
  if (messages.length === 0) return <div className="text-center text-slate-400 text-sm py-10">No {notes ? "internal notes" : "messages"} yet.</div>;
  const isEmailChannel = channel === "email";

  return (
    <div className={`space-y-3 ${isEmailChannel ? "max-w-4xl mx-auto" : "max-w-3xl mx-auto"}`}>
      {messages.map((m) => {
        const out = m.direction === "outbound";
        const cm = ch(m.channel || channel);
        if (notes) {
          const isAllison = m.sender_type === "allison";
          return (
            <div key={m.id} className={`rounded-lg p-3 text-sm border ${isAllison ? "bg-indigo-50 border-indigo-200" : "bg-amber-50 border-amber-200"}`} data-testid={isAllison ? `cc-allison-${m.id}` : `cc-note-${m.id}`}>
              <div className={`text-xs font-medium mb-0.5 flex items-center gap-1 ${isAllison ? "text-indigo-700" : "text-amber-700"}`}>
                {isAllison ? <Icons.Sparkles className="h-3 w-3" /> : <Icons.Lock className="h-3 w-3" />}
                {m.sender_name} · {new Date(m.sent_at || m.created_at).toLocaleString()}
              </div>
              <div className="text-slate-700 whitespace-pre-wrap">{m.body}</div>
            </div>
          );
        }
        // Email channel: use professional email card rendering
        if (isEmailChannel || m.channel === "email") {
          return (
            <EmailMessageCard
              key={m.id}
              m={m}
              onReply={!out ? onReply : null}
              onReplyAll={!out ? onReplyAll : null}
              onForward={!out ? onForward : null}
              onTrash={!out ? onTrash : null}
            />
          );
        }
        // Other channels: chat-bubble style
        return (
          <div key={m.id} className={`flex gap-2 ${out ? "flex-row-reverse" : ""}`} data-testid={`cc-msg-${m.id}`}>
            <Avatar name={m.sender_name} size="h-7 w-7" color={out ? "bg-blue-500" : "bg-slate-400"} />
            <div className={`max-w-[70%] ${out ? "items-end" : "items-start"} flex flex-col`}>
              <div className={`rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${out ? "bg-blue-600 text-white rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"}`}>{m.body}</div>
              <div className="text-[10px] text-slate-400 mt-1 px-1">{new Date(m.sent_at || m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · {cm.label}{out ? " ✓✓" : ""}</div>
            </div>
          </div>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
}

function AttachmentsTab({ documents, cid }) {
  const fmtBytes = (b) => !b ? "" : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`;
  const fmtDate = (s) => s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";
  const srcLabel = (s) => s === "titan_live_sync" ? "Email Inbound" : s === "comm_draft" ? "Sent Email" : s === "staff_upload" ? "Staff Upload" : s || "Unknown";

  if (!documents || documents.length === 0)
    return <div className="text-center text-slate-400 text-sm py-10">No attachments for this conversation.</div>;

  const handleDownload = async (doc) => {
    try {
      const res = await api.get(`/communications/conversations/${cid}/attachments/${doc.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.display_filename || doc.original_filename || doc.id;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed.");
    }
  };

  return (
    <div className="space-y-2 max-w-2xl mx-auto" data-testid="cc-attachments">
      {documents.map((d, i) => {
        const name = d.display_filename || d.original_filename || "Attachment";
        const canDownload = d.content_available === true || !!d.storage_path;
        return (
          <div key={d.id || i} data-testid={`cc-attachment-row-${i}`}
            className="bg-white border border-slate-200 rounded-lg px-4 py-3 flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
              <Icons.Paperclip className="h-4 w-4 text-blue-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-800 truncate">{name}</div>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className="text-[10px] uppercase tracking-wide text-slate-400">{d.category}</span>
                {d.size_bytes ? <span className="text-[10px] text-slate-400">{fmtBytes(d.size_bytes)}</span> : null}
                {d.source ? <span className="text-[10px] text-slate-400">{srcLabel(d.source)}</span> : null}
                {d.created_at ? <span className="text-[10px] text-slate-400">{fmtDate(d.created_at)}</span> : null}
              </div>
            </div>
            {canDownload ? (
              <button onClick={() => handleDownload(d)} data-testid={`cc-attachment-download-${i}`}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg px-2.5 py-1.5 transition-colors shrink-0">
                <Icons.Download className="h-3.5 w-3.5" />Download
              </button>
            ) : (
              <span className="text-[10px] text-slate-400 shrink-0">Content unavailable</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SimpleList({ items, render, empty, testid }) {
  if (!items || items.length === 0) return <div className="text-center text-slate-400 text-sm py-10">{empty}</div>;
  return <div className="space-y-2 max-w-2xl mx-auto" data-testid={testid}>{items.map((it, i) => <div key={it.id || i} className="bg-white border border-slate-200 rounded-lg px-3.5 py-2.5 text-sm text-slate-700">{render(it)}</div>)}</div>;
}

function Timeline({ clientId }) {
  const [events, setEvents] = useState(null);
  useEffect(() => { if (clientId) api.get(`/timeline?entity_type=client&entity_id=${clientId}&limit=30`).then((r) => setEvents(r.data.items || r.data || [])).catch(() => setEvents([])); else setEvents([]); }, [clientId]);
  if (events === null) return <div className="py-10 text-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500 inline" /></div>;
  if (events.length === 0) return <div className="text-center text-slate-400 text-sm py-10">No timeline events.</div>;
  return (
    <div className="max-w-2xl mx-auto space-y-3" data-testid="cc-timeline">
      {events.map((e, i) => (
        <div key={e.id || i} className="flex gap-3"><div className="h-7 w-7 rounded-full bg-slate-100 flex items-center justify-center shrink-0"><Icons.Circle className="h-3 w-3 text-slate-400" /></div>
          <div><div className="text-sm text-slate-700">{e.title}</div>{e.description && <div className="text-xs text-slate-400">{e.description}</div>}<div className="text-[10px] text-slate-400">{new Date(e.created_at).toLocaleString()}</div></div></div>
      ))}
    </div>
  );
}

function ActionDialog({ dialog, cid, clientId, onClose, onDone }) {
  const [form, setForm] = useState({ title: "", description: "", priority: "normal", reason: "", target: "case_manager", assigned_user_id: "" });
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (dialog.type === "assign") api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {}); }, [dialog.type]);

  const submit = async () => {
    setBusy(true);
    try {
      if (dialog.type === "task") await api.post(`/communications/conversations/${cid}/task`, { title: form.title, priority: form.priority, due_at: null });
      else if (dialog.type === "ir") await api.post(`/communications/conversations/${cid}/information-request`, { title: dialog.doc ? `Document request: ${form.title}` : form.title, description: form.description, why_needed: null, priority: form.priority, due_date: null });
      else if (dialog.type === "assign") await api.post(`/communications/conversations/${cid}/assign`, { assigned_user_id: form.assigned_user_id });
      else if (dialog.type === "escalate") await api.post(`/communications/conversations/${cid}/escalate`, { target: form.target, note: form.reason });
      toast.success("Done"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } finally { setBusy(false); }
  };
  const T = { task: "Create Task", ir: dialog.doc ? "Request Documents" : "Create Information Request", assign: "Assign Conversation", escalate: "Escalate Conversation" }[dialog.type];
  const cls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm";

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="cc-action-dialog"><DialogHeader><DialogTitle>{T}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {(dialog.type === "task" || dialog.type === "ir") && <input className={cls} placeholder={dialog.doc ? "What documents are needed?" : "Title"} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="cc-dialog-title" />}
          {dialog.type === "ir" && <textarea className={cls} rows={2} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />}
          {(dialog.type === "task" || dialog.type === "ir") && <select className={cls} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option></select>}
          {dialog.type === "assign" && <select className={cls} value={form.assigned_user_id} onChange={(e) => setForm({ ...form, assigned_user_id: e.target.value })} data-testid="cc-dialog-assignee"><option value="">Select staff…</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select>}
          {dialog.type === "escalate" && <><select className={cls} value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })}>{["case_manager", "team_lead", "partner", "finance", "production", "client_success"].map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}</select><textarea className={cls} rows={2} placeholder="Reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="cc-dialog-reason" /></>}
        </div>
        <DialogFooter><button onClick={submit} disabled={busy} data-testid="cc-dialog-submit" className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50">{busy ? "Working…" : "Confirm"}</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
